import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import PublicSite from './site/PublicSite';
import { isAppEntryRoute, isAppEntryAlias, isPublicSiteRoute } from './routes/appRoutes';
import {
  AuthPage,
  onAuthStateChanged,
  auth,
  firebaseSignOut,
} from './auth';
import CreateProfilePage from './pages/CreateProfilePage';
import AdminFinancePage from './pages/AdminFinancePage';
import AdminGroupsPage from './pages/AdminGroupsPage';
import AdminRouteErrorBoundary from './components/admin/AdminRouteErrorBoundary';
import GroupsPage from './pages/GroupsPage';
import GroupDetailPage from './pages/GroupDetailPage';
import JoinLeaguePage from './pages/JoinLeaguePage';
import ChallengeTicketPage from './pages/ChallengeTicketPage';
import ChallengeTicketRouteErrorBoundary from './components/ticket/ChallengeTicketRouteErrorBoundary';
import { peekPendingJoinToken } from './utils/joinLeagueIntent';
import {
  peekPendingTicketCode,
  buildPendingTicketResumePath,
  logTicketResume,
} from './utils/challengeTicketIntent';
import { createChallengeTicket } from './api/challengeTicketService';
import {
  getChallengeSendErrorMessage,
  getOpenChallengePostErrorMessage,
} from './utils/challengeErrors';
import {
  fetchUserProfile,
  waitForUserProfileCondition,
  applyDisputePenaltyOnServer,
  hasRealProfile as profileIsComplete,
  mergeUserProfile,
  markOnboardingCompleted,
} from './api/userProfileService';
import { useHomepageData } from './hooks/useHomepageData';
import {
  clearGuestSensitiveLocalStorage,
  clearLocalUserSession,
  deleteUserAccountPermanently,
  getAccountReauthMethod,
} from './api/accountService';
import AppBootFallback from './components/common/AppBootFallback';
import GuestWelcomeTicket from './components/common/GuestWelcomeTicket';
import {
  isGuestWelcomeTicketDismissed,
  persistGuestWelcomeTicketDismissed,
} from './utils/guestWelcomeTicketStorage';
import {
  shouldChainTourAfterGuestWelcomeDismiss,
  shouldDeferTourAutoShow,
  guestWelcomeVisible,
  howToPlayVisible,
} from './utils/onboardingOverlayPolicy';
import { HowToPlayModal, WinnerNotificationModal, SuspensionGate } from './components/modals';
import StadiumShell from './layouts/StadiumShell';
import { useRoutePerf } from './hooks/useRoutePerf';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useChatPresence } from './hooks/useChatPresence';
import { useNotificationCenter } from './hooks/useNotificationCenter';
import { useGroupInvites } from './hooks/useGroupInvites';
import { useGroupMemberships } from './hooks/useGroupMemberships';
import AppToast from './components/common/AppToast';
import GuidedSpotlightTour from './components/onboarding/GuidedSpotlightTour';
import { buildGuidedTourSteps, MIN_TOUR_FUNDS } from './utils/guidedTourSteps';
import {
  isOnboardingCompleted,
  markGuestOnboardingSeen,
  markOnboardingCompletedLocal,
} from './utils/onboardingStorage';
import { notificationBus } from './notifications/notificationEventBus';
import { NotificationType, NotificationPriority } from './notifications/notificationTypes';
import { dismissBootSplash, forceDismissBootSplash } from './pwa/bootSplash';
import { closeTopModal } from './utils/modalStack';
import { perfMarkStart, perfMarkEnd, perfTimeline } from './utils/startupPerf';
import { loadCachedProfile, saveCachedProfile } from './utils/profileCache';
import {
  subscribeUserChallenges,
  buildChallengeRef,
  createChallenge,
  acceptChallenge,
  declineChallenge,
  agreeCustomChallenge,
  counterChallenge,
  updateChallenge,
  removeChallenge,
  recordChallengeFinalOutcome,
  ChallengeStatus,
  CHALLENGES_COLLECTION,
} from './api/challengeService';
import { logChallengeLifecycle } from './utils/challengeLifecycle';
import { isTerminalChallenge } from './utils/challengePhase';
import {
  LifecycleTransition,
  MY_ARENA_COMPLETED_NAV,
  MY_ARENA_SENT_NAV,
  buildLifecycleTransitionPayload,
} from './utils/lifecycleTransitionNotify';
import {
  NotificationClickAction,
  resolvePushNotificationClick,
} from './utils/notificationClickRouting';
import { COMPLETED_VIEW_IN_ARENA } from './constants/productCopy';
import {
  buildOpenChallengeRef,
  writeOpenChallenge,
  acceptOpenChallengeTransaction,
  cancelOpenChallenge,
  revertOpenChallengeAccept,
  OPEN_CHALLENGES_COLLECTION,
} from './api/openChallengeService';
import { collection, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import {
  buildResolutionFromBet,
  buildResolutionFromCustomVote,
  stripClientFeeFields,
  formatWinnerNotificationMessage,
  formatLoserLiveNotificationMessage,
  escrowPayoutAmount,
} from './utils/challengeWinner';
import {
  loadPlayerStats,
  savePlayerStats,
  computePlayerStatsFromSettledBets,
  applyBetResultToStats,
} from './utils/playerStats';
import {
  loadWallet,
  saveWalletMirror,
  syncWalletFromProfile,
  createDefaultWalletState,
  getActiveWallet,
  mapWalletToUserFields,
  mutateActiveWallet,
  deductStake,
  canAfford,
  isValidWagerStake,
  isWithinMaxWager,
  maxWagerExceededMessage,
  insufficientFundsMessage,
  creditStake,
  applyBetSettlementToState,
  refundParticipantStakeOnState,
} from './api/walletService';
import {
  deductWalletStake,
  creditWalletPayout,
  refundWalletStake,
  settleChallengeOnServer,
} from './api/walletApi';
import {
  hasLiveSubscriptionAccess,
  getSubscriptionTier,
  canUsePrivateChat,
  canAddFriendsUnlimited,
  canSeeScoreStreaks,
  canUseCustomWagers,
  SUBSCRIPTION_REQUIRED_MESSAGE,
} from './utils/subscriptionAccess';
import {
  canSendChallenge,
  customSendBlockReason,
  getCustomSendUpgradeCopy,
  ENTITLEMENT_COPY,
} from './utils/entitlements';
import {
  canAddFreeFriend,
  countUserAddedFriends,
  FREE_FRIEND_LIMIT_MESSAGE,
  FREE_PRIVATE_CHAT_MESSAGE,
} from './constants/freeTierLimits';
import { getTierById } from './constants/subscriptionTiers';
import {
  BILLING_PORTAL_REDIRECT_TOAST,
  SUBSCRIPTION_REDIRECT_TOAST,
} from './constants/productCopy';
import { CHECKOUT_SOURCES } from './constants/checkoutAttribution';
import {
  peekOpenCreateGroupPending,
  setCheckoutIntent,
  setOpenCreateGroupAfterCheckout,
} from './utils/checkoutIntent';
import {
  createSubscriptionCheckout,
  createBillingPortalSession,
  recordMatchTransaction,
  isStripeConfigured,
  syncSubscriptionAfterCheckout,
  syncWalletDepositAfterCheckout,
  isActiveSubscriptionStatus,
} from './api/stripeService';
import { stripLegacySystemFriends } from './utils/morpheusFriend';
import {
  subscribeIncomingFriendRequests,
  subscribeOutgoingFriendRequests,
  subscribeDeclinedSentFriendRequests,
  subscribeFriendships,
  sendFriendRequest,
  acceptFriendRequest,
  declineFriendRequest,
  setFriendBlocked,
  unblockAndResetRelationship,
  removeFriendship,
  cancelOutgoingFriendRequest,
  areFriends,
  FRIEND_LIMIT_REACHED,
} from './api/friendService';
import {
  usePrivateChatInbox,
  tryOpenPrivateChat,
  isMessagingBlockedWithPlayer,
} from './privateChat';
import {
  GUEST_USER_ID,
  LEGACY_MOCK_BET_IDS,
  LEGACY_MOCK_USER_IDS,
  canReceiveFriendRequest,
  purgeLegacyChatLocalStorage,
} from './utils/legacyMockUsers';
import {
  buildAccessContext,
  canCreateStandardChallenge,
  canStartStripeCheckout,
} from './utils/accessControl';
import { adminRouteKind, isAdminRoute } from './routes/adminRoutes';
import { applyAdminDocumentTheme } from './utils/adminDocumentTheme';
import { adminDiag } from './utils/adminDiagnostics';
import GuestRegistrationPrompt from './components/common/GuestRegistrationPrompt';
import './App.css';

const Lockbox = lazy(() => {
  perfMarkStart('lockbox.chunkLoad');
  return import('./unAuth/components/nextpage1').then((mod) => {
    perfMarkEnd('lockbox.chunkLoad', 'chunk');
    return mod;
  });
});

function friendsStorageKey(uid) {
  return `lockedin_friends_${uid}`;
}

function riskTallyEscalationKey(betId, uid) {
  return `${betId}:${uid}`;
}

const DISPUTE_LOCK_MS = 24 * 60 * 60 * 1000;

const LOCKBOX_ENTERED_KEY = 'lockedin_lockbox_entered';

function readInitialEntered() {
  try {
    if (sessionStorage.getItem(LOCKBOX_ENTERED_KEY) === '1') return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get('entered') === '1') return true;
  } catch (_) {}
  return false;
}

function readStripeCheckoutReturn() {
  try {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('deposit') || params.get('subscription'));
  } catch (_) {
    return false;
  }
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  useRoutePerf();
  const [entered, setEntered] = useState(readInitialEntered);
  const enteredRef = useRef(entered);
  enteredRef.current = entered;
  const walletChromeRef = useRef({ openWallet: null });
  const stripeCheckoutReturn = useMemo(readStripeCheckoutReturn, []);
  const isCreateProfileRoute = location.pathname === '/create-profile';
  const adminRoute = adminRouteKind(location.pathname);
  const onAppEntryRoute = isAppEntryRoute(location.pathname);
  const onPublicSiteRoute = isPublicSiteRoute(location.pathname);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showGuestWelcome, setShowGuestWelcome] = useState(false);
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [privateChatFriend, setPrivateChatFriend] = useState(null);

  const AVATAR_STORAGE_KEY = 'lockedin_user_avatar';

  const loadStoredAvatar = () => {
    try {
      const url = window.localStorage.getItem(AVATAR_STORAGE_KEY);
      if (url && typeof url === 'string') return url;
    } catch (_) {}
    return null;
  };

  const guestUser = {
    id: GUEST_USER_ID,
    name: 'Guest',
    walletBalance: 0,
  };

  const [friends, setFriends] = useState([]);
  const [incomingFriendRequests, setIncomingFriendRequests] = useState([]);
  const [outgoingFriendRequests, setOutgoingFriendRequests] = useState([]);
  const challengesHydratedRef = useRef(false);
  const declinedChallengeNotifiedRef = useRef(new Set());
  const declinedFriendRequestNotifiedRef = useRef(new Set());
  const statsRecordedBetIdsRef = useRef(new Set());
  const [playerStats, setPlayerStats] = useState({});
  const [playerStatsReady, setPlayerStatsReady] = useState(false);
  const [wallet, setWallet] = useState(() => loadWallet(null));
  const [userAvatar, setUserAvatar] = useState(loadStoredAvatar);
  const [blockedFriendIds, setBlockedFriendIds] = useState(new Set());
  const [hiddenPlayerIds, setHiddenPlayerIds] = useState(new Set());
  const [unreadByFriendId, setUnreadByFriendId] = useState({});
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    winner: null,
    chatFrom: null,
  });
  const [toast, setToast] = useState(null);
  const [openFriendsSignal, setOpenFriendsSignal] = useState(0);
  const [openPendingInboxSignal, setOpenPendingInboxSignal] = useState(0);
  const [bootstrapAction, setBootstrapAction] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const justCreatedProfileRef = useRef(false);
  const finalOutcomeNotifiedRef = useRef(new Set());
  const settlementNotifiedRef = useRef(new Set());
  const [userStrikes, setUserStrikes] = useState(0);
  const [suspensionEndsAt, setSuspensionEndsAt] = useState(null);
  const [accountLockEndsAt, setAccountLockEndsAt] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const firebaseUserRef = useRef(firebaseUser);
  firebaseUserRef.current = firebaseUser;
  const [authReady, setAuthReady] = useState(false);
  const [profileDoc, setProfileDoc] = useState(undefined);
  const [stripeCheckoutLoading, setStripeCheckoutLoading] = useState(null);
  const enableDataSubscriptions = entered;
  const enableProfileSubscription =
    Boolean(firebaseUser?.uid)
    && (
      enableDataSubscriptions
      || isCreateProfileRoute
      || stripeCheckoutReturn
      || (authReady && location.pathname === '/')
    );

  const enableHomepage = entered || isCreateProfileRoute || stripeCheckoutReturn;

  const homepage = useHomepageData({
    enabled: enableHomepage,
    uid: firebaseUser?.uid ?? null,
  });

  const notificationCenter = useNotificationCenter(firebaseUser?.uid ?? null);
  const pendingGroupInvites = useGroupInvites(firebaseUser?.uid);
  const groupMemberships = useGroupMemberships(firebaseUser?.uid);

  const showToast = useCallback((message, variant = 'error') => {
    if (message && typeof message === 'object' && 'message' in message) {
      setToast({ ...message, variant: message.variant ?? variant });
      return;
    }
    setToast({ message, variant });
  }, []);

  const emitNotification = useCallback((event) => {
    const uid = firebaseUser?.uid;
    if (!uid) return;
    notificationBus.emit(uid, event);
  }, [firebaseUser?.uid]);

  /** Terminal transition → toast + notification center, pointing at Completed (WI-3 Step 2). */
  const notifyCompletedTransition = useCallback((transition, bet, opts = {}) => {
    const payload = buildLifecycleTransitionPayload(transition, bet, opts);
    if (!payload) return;
    showToast({
      title: payload.toastTitle,
      message: payload.toastMessage,
      variant: payload.toastVariant,
    });
    emitNotification({
      type: payload.notificationType,
      title: payload.notificationTitle,
      body: `${payload.notificationBody} ${payload.viewCta || COMPLETED_VIEW_IN_ARENA}`,
      priority: payload.priority,
      dedupeKey: payload.dedupeKey,
      meta: payload.meta,
    });
  }, [showToast, emitNotification]);

  const handleNotificationAction = useCallback((item) => {
    notificationCenter.setOpen(false);
    const friendId = item.meta?.friendId;
    if (item.type === NotificationType.MESSAGE && friendId) {
      const friend = friends.find((f) => f.id === friendId);
      setPrivateChatFriend(friend || { id: friendId, name: item.title });
      return;
    }
    if (item.type === NotificationType.FRIEND_REQUEST) {
      setOpenFriendsSignal((n) => n + 1);
      return;
    }
    if (item.type === NotificationType.CHALLENGE || item.type === NotificationType.SYSTEM) {
      if (item.meta?.navigate === MY_ARENA_COMPLETED_NAV
        || item.meta?.navigate === MY_ARENA_SENT_NAV) {
        setBootstrapAction(item.meta.navigate);
        return;
      }
      setOpenPendingInboxSignal((n) => n + 1);
    }
  }, [friends, notificationCenter]);

  useChatPresence(firebaseUser?.uid ?? null, privateChatFriend?.id ?? null);

  useEffect(() => {
    if (!firebaseUser?.uid || typeof navigator === 'undefined' || !navigator.serviceWorker) return undefined;
    const onMessage = (event) => {
      const msg = event?.data;
      if (msg?.type !== 'NOTIFICATION_CLICK') return;
      // Push deep-link parity (Sprint 11 WI-4): route a push click via the same
      // pure resolver the in-app notification center uses, so meta_navigate
      // lands the user on the tab where the item is visible — not the inbox.
      const decision = resolvePushNotificationClick(msg.data || {});
      switch (decision.action) {
        case NotificationClickAction.PRIVATE_CHAT: {
          const friend = friends.find((f) => f.id === decision.friendId);
          setPrivateChatFriend(friend || { id: decision.friendId, name: decision.title });
          break;
        }
        case NotificationClickAction.OPEN_FRIENDS:
          setOpenFriendsSignal((n) => n + 1);
          break;
        case NotificationClickAction.BOOTSTRAP_NAV:
          setBootstrapAction(decision.target);
          break;
        case NotificationClickAction.PENDING_INBOX:
          setOpenPendingInboxSignal((n) => n + 1);
          break;
        default:
          break;
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [firebaseUser?.uid, friends]);

  const authPerfLoggedRef = useRef(false);
  const profilePerfLoggedRef = useRef(false);
  const socialPerfLoggedRef = useRef(false);

  const SECOND_CHANCE_MS = 15 * 60 * 1000; // custom prop bets: re-vote window
  const secondChanceEscalatedBetIdsRef = useRef(new Set());
  const riskTalliedBetIdsRef = useRef(new Set());
  const riskTallyByIdRef = useRef(new Map());

  useEffect(() => {
    purgeLegacyChatLocalStorage();
    perfMarkEnd('js.bundle', 'render');
    perfMarkStart('app.firstRender');

    const splashFailsafe = window.setTimeout(() => {
      forceDismissBootSplash();
    }, 3000);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        perfMarkEnd('app.firstRender', 'render');
        perfTimeline('app.firstPaint');
        perfMarkEnd('pwa.firstMeaningfulPaint', 'pwa');

        dismissBootSplash().then(() => {
          perfMarkEnd('pwa.timeToInteractive', 'pwa');
          perfTimeline('pwa.timeToInteractive', 'pwa');
        });
      });
    });

    return () => window.clearTimeout(splashFailsafe);
  }, []);

  useEffect(() => {
    applyAdminDocumentTheme(location.pathname);
    return () => {
      if (!isAdminRoute(location.pathname)) applyAdminDocumentTheme('');
    };
  }, [location.pathname]);

  useEffect(() => {
    if (adminRoute) {
      adminDiag('admin_route_detected');
    }
  }, [adminRoute]);

  // Lockbox lives outside React Router — push a history entry so browser Back exits cleanly.
  useEffect(() => {
    if (!entered) return;
    if (window.history.state?.lockbox) return;
    try {
      window.history.pushState({ lockbox: true }, '', window.location.href);
    } catch (_) {}
  }, [entered]);

  useEffect(() => {
    perfMarkStart('auth.onAuthStateChanged');
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!authPerfLoggedRef.current) {
        authPerfLoggedRef.current = true;
        perfMarkEnd('auth.onAuthStateChanged', 'auth', { uid: user?.uid ?? null });
        perfTimeline('auth.ready', 'auth', { uid: user?.uid ?? null });
      }
      setFirebaseUser(user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    try {
      if (entered) sessionStorage.setItem(LOCKBOX_ENTERED_KEY, '1');
      else sessionStorage.removeItem(LOCKBOX_ENTERED_KEY);
    } catch (_) {}
  }, [entered]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid) {
      setProfileDoc(undefined);
      return;
    }
    if (!enableProfileSubscription) return;

    const cached = loadCachedProfile(uid);
    if (cached.profile && profileDoc === undefined) {
      setProfileDoc(cached.profile);
      perfTimeline('profile.cacheHit', 'firestore', { uid });
    }
  }, [firebaseUser?.uid, enableProfileSubscription]);

  // Live profile + wallet — merged from API hydrate + Firestore listener (no polling).
  useEffect(() => {
    if (!enableProfileSubscription || !homepage.profile) return;
    const incoming = homepage.profile;
    if (!profilePerfLoggedRef.current) {
      profilePerfLoggedRef.current = true;
      perfTimeline('profile.homepageHydrated', 'api');
    }
    setProfileDoc((prev) => mergeUserProfile(prev, incoming));
    setWallet((prev) => syncWalletFromProfile(prev, incoming));
  }, [homepage.profile, enableProfileSubscription]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subResult = params.get('subscription');
    const depositResult = params.get('deposit');
    if ((!subResult && !depositResult) || !firebaseUser?.uid) return undefined;

    let cancelled = false;

    const applyProfile = (doc) => {
      if (!doc) return;
      setProfileDoc(doc);
      setWallet((prev) => syncWalletFromProfile(prev, doc));
    };

    const waitForSubscriptionReturn = async () => {
      const sessionId = params.get('session_id');
      if (subResult === 'success' && sessionId) {
        try {
          await syncSubscriptionAfterCheckout(sessionId);
        } catch (err) {
          console.warn('syncSubscriptionAfterCheckout:', err);
        }
      }

      try {
        const doc = await waitForUserProfileCondition(
          firebaseUser.uid,
          (p) => isActiveSubscriptionStatus(p?.subscriptionStatus),
          { timeoutMs: 30000 }
        );
        applyProfile(doc);
        return doc;
      } catch (err) {
        console.warn('waitForSubscriptionReturn:', err);
        const doc = await fetchUserProfile(firebaseUser.uid);
        applyProfile(doc);
        return doc;
      }
    };

    const waitForDepositReturn = async () => {
      const sessionId = params.get('session_id');
      const initialDoc = profileDoc && profileDoc !== undefined
        ? profileDoc
        : await fetchUserProfile(firebaseUser.uid);
      const initialBalance = initialDoc?.walletBalance ?? 0;
      applyProfile(initialDoc);

      let syncedBalance = null;
      if (depositResult === 'success' && sessionId) {
        try {
          const result = await syncWalletDepositAfterCheckout(sessionId);
          if (result?.walletBalance != null) {
            syncedBalance = result.walletBalance;
          }
        } catch (err) {
          console.warn('syncWalletDepositAfterCheckout:', err);
        }
      }

      try {
        const doc = await waitForUserProfileCondition(
          firebaseUser.uid,
          (p) => {
            const balance = p?.walletBalance ?? 0;
            return balance > initialBalance
              || (syncedBalance != null && balance >= syncedBalance);
          },
          { timeoutMs: 30000 }
        );
        return { doc, credited: (doc?.walletBalance ?? 0) > initialBalance };
      } catch (err) {
        console.warn('waitForDepositReturn:', err);
        const doc = await fetchUserProfile(firebaseUser.uid);
        return { doc, credited: (doc?.walletBalance ?? 0) > initialBalance };
      }
    };

    const finishReturn = (result) => {
      if (cancelled) return;
      const doc = result?.doc ?? result;
      const depositCredited = result?.credited;
      if (subResult === 'success') {
        setEntered(true);
        const tierId = params.get('tier') || doc?.subscriptionTier;
        const tierName = getTierById(tierId)?.name || 'Your plan';
        const active = isActiveSubscriptionStatus(doc?.subscriptionStatus);
        const trialing = doc?.subscriptionStatus === 'trialing';
        showToast(
          active
            ? (trialing
              ? `${tierName} trial started — full access for 7 days.`
              : `${tierName} is active.`)
            : `${tierName} payment received — your plan is activating. If features don't unlock within a minute, contact support.`,
          'success'
        );
        if (peekOpenCreateGroupPending()) {
          navigate('/groups');
        }
      } else if (subResult === 'cancel') {
        showToast('Checkout canceled — pick a plan anytime in Wallet.')
      } else if (depositResult === 'success') {
        setEntered(true);
        const balance = doc?.walletBalance ?? 0;
        showToast(
          depositCredited
            ? `Deposit confirmed — wallet balance is $${balance.toFixed(2)}.`
            : 'Payment received — your wallet is updating. If your balance does not change within a minute, contact support.',
          depositCredited ? 'success' : 'critical'
        );
      } else if (depositResult === 'cancel') {
        showToast('Deposit canceled — add funds anytime in Wallet.')
      }
      window.history.replaceState({}, '', window.location.pathname);
    };

    const waitPromise = subResult
      ? waitForSubscriptionReturn().then((doc) => ({ doc }))
      : waitForDepositReturn();

    waitPromise
      .then(finishReturn)
      .catch((err) => {
        console.warn('stripe checkout return handler:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid]);

  useEffect(() => {
    const uid = firebaseUser?.uid || null;
    if (profileDoc) {
      setWallet((prev) => syncWalletFromProfile(prev, profileDoc));
      return;
    }
    setWallet(loadWallet(uid, profileDoc));
  }, [firebaseUser?.uid, profileDoc]);

  useEffect(() => {
    const lockTs = Number(profileDoc?.accountLockEndsAt) || null;
    setAccountLockEndsAt(lockTs);
  }, [profileDoc?.accountLockEndsAt]);

  useEffect(() => {
    const uid = firebaseUser?.uid ?? null;
    if (!uid) return;
    saveWalletMirror(uid, wallet);
  }, [wallet, firebaseUser?.uid]);

  useEffect(() => {
    const socialUid = firebaseUser?.uid;

    if (!socialUid || !enableDataSubscriptions) {
      if (!socialUid) {
        setFriends([]);
      }
      setIncomingFriendRequests([]);
      setOutgoingFriendRequests([]);
      return undefined;
    }

    perfMarkStart('firestore.socialListeners');
    const unsubFriends = subscribeFriendships(
      socialUid,
      (list) => {
          if (!socialPerfLoggedRef.current) {
            socialPerfLoggedRef.current = true;
            perfMarkEnd('firestore.socialListeners', 'firestore', { friendCount: list.length });
          }
          const mapped = stripLegacySystemFriends(
            list.map((f) => ({
              ...f,
              riskTally: riskTallyByIdRef.current.get(f.id) ?? 0,
            }))
          );
          setFriends(mapped);
        },
      () => showToast('Could not load friends. Check Firestore rules.')
    );

    const unsubIncoming = subscribeIncomingFriendRequests(
      firebaseUser.uid,
      setIncomingFriendRequests
    );

    const unsubOutgoing = subscribeOutgoingFriendRequests(
      firebaseUser.uid,
      setOutgoingFriendRequests
    );

    declinedFriendRequestNotifiedRef.current = new Set();

    const unsubDeclinedSent = subscribeDeclinedSentFriendRequests(
      firebaseUser.uid,
      (requests) => {
        requests.forEach((r) => declinedFriendRequestNotifiedRef.current.add(r.id));
      }
    );

    return () => {
      socialPerfLoggedRef.current = false;
      unsubFriends();
      unsubIncoming();
      unsubOutgoing();
      unsubDeclinedSent();
    };
  }, [firebaseUser?.uid, enableDataSubscriptions]);

  useEffect(() => {
    setBlockedFriendIds(new Set(friends.filter((f) => f.blockedByMe).map((f) => f.id)));
    setHiddenPlayerIds(
      new Set(friends.filter((f) => f.blockedByMe || f.blocksMe).map((f) => f.id))
    );
  }, [friends]);

  // Enrich friends with persisted risk tally so other players see risk markers.
  useEffect(() => {
    if (!enableDataSubscriptions || !firebaseUser?.uid || friends.length === 0) return;

    const ids = friends.map((f) => f.id).filter(Boolean);
    const missing = ids.filter((id) => !riskTallyByIdRef.current.has(id));
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        missing.map(async (id) => {
          try {
            const p = await fetchUserProfile(id);
            return [id, p?.riskTally ?? 0];
          } catch {
            return [id, 0];
          }
        })
      );
      if (cancelled) return;

      entries.forEach(([id, riskTally]) => {
        riskTallyByIdRef.current.set(id, riskTally);
      });

      setFriends((prev) =>
        prev.map((f) => {
          const riskTally = riskTallyByIdRef.current.get(f.id);
          return riskTally == null ? f : { ...f, riskTally };
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [enableDataSubscriptions, firebaseUser?.uid, friends]);

  useEffect(() => {
    if (!enableDataSubscriptions || !firebaseUser?.uid || friends.length === 0) return;
    try {
      window.localStorage.setItem(friendsStorageKey(firebaseUser.uid), JSON.stringify(friends));
    } catch (_) {}
  }, [friends, firebaseUser?.uid]);

  useEffect(() => {
    if (
      privateChatFriend?.id
      && isMessagingBlockedWithPlayer(privateChatFriend.id, friends)
    ) {
      setPrivateChatFriend(null);
    }
  }, [friends, privateChatFriend?.id]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid) {
      setPlayerStats({});
      setPlayerStatsReady(true);
      return;
    }
    setPlayerStatsReady(false);
    setPlayerStats(loadPlayerStats(uid));
  }, [firebaseUser?.uid]);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid) return;
    savePlayerStats(uid, playerStats);
  }, [playerStats, firebaseUser?.uid]);

  const activeWalletBalance = getActiveWallet(wallet).balance;

  const notifyInsufficientFunds = (stake) => {
    showToast(insufficientFundsMessage(stake, activeWalletBalance), 'critical');
  };

  const validateStakeForPlay = (stake) => {
    if (!canCreateStandardChallenge(accessCtx)) {
      showGuestRegistrationPrompt();
      return false;
    }
    if (!ensureAccountUnlocked()) return false;
    const amount = Number(stake);
    if (!isValidWagerStake(amount)) {
      showToast('Enter a valid challenge amount greater than $0.')
      return false;
    }
    if (!isWithinMaxWager(amount)) {
      showToast(maxWagerExceededMessage())
      return false;
    }
    if (canAfford(activeWalletBalance, amount)) return true;
    notifyInsufficientFunds(amount);
    return false;
  };

  const refreshProfileAndWallet = useCallback(async () => {
    if (!firebaseUser?.uid) return null;
    return homepage.profile ?? null;
  }, [firebaseUser?.uid, homepage.profile]);

  const deductStakeForPlay = async (stake, matchId, description = 'Challenge entry') => {
    if (!validateStakeForPlay(stake)) return false;
    if (!canCreateStandardChallenge(accessCtx)) {
      showGuestRegistrationPrompt();
      return false;
    }
    if (!firebaseUser?.uid) {
      showGuestRegistrationPrompt();
      return false;
    }

    const snapshot = wallet;
    setWallet((prev) => mutateActiveWallet(prev, (w) => deductStake(w, stake)) ?? prev);
    try {
      await deductWalletStake(stake, description, matchId);
      await refreshProfileAndWallet();
      return true;
    } catch (err) {
      setWallet(snapshot);
      showToast(err?.message || 'Could not lock challenge funds.')
      return false;
    }
  };

  const refundStakeToWallet = useCallback(async (stake, matchId, description = 'Challenge refund') => {
    if (!stake) return;
    if (firebaseUser?.uid) {
      try {
        await refundWalletStake(stake, description, matchId);
        await refreshProfileAndWallet();
      } catch (err) {
        console.warn('refundWalletStake:', err);
        setWallet((prev) => mutateActiveWallet(prev, (w) => creditStake(w, stake)) ?? prev);
      }
    }
  }, [firebaseUser?.uid, refreshProfileAndWallet]);

  const refundCreatorStakeForBet = async (bet) => {
    if (!bet || bet.creatorId !== userWithFriends.id) return;
    const stake = bet.participants?.find((p) => p.id === bet.creatorId)?.amount;
    if (!stake) return;
    await refundStakeToWallet(stake, bet.id, 'Creator entry refund');
  };

  const recordBetResult = useCallback(async (bet, resolution) => {
    if (!bet?.participants?.length || !resolution || resolution.isDraw) return;
    if (statsRecordedBetIdsRef.current.has(bet.id)) return;
    statsRecordedBetIdsRef.current.add(bet.id);
    if (!useCloudChallenges) {
      setPlayerStats((prev) => applyBetResultToStats(prev, bet, resolution));
    }
    const uid = firebaseUser?.uid || GUEST_USER_ID;

    if (useCloudChallenges && firebaseUser?.uid) {
      if (resolution.winningPlayerId === uid) {
        try {
          await refreshProfileAndWallet();
        } catch (err) {
          console.warn('refreshProfileAndWallet after settlement:', err);
        }
      }
    } else if (firebaseUser?.uid && resolution.winningPlayerId === uid) {
      const payout = resolution.payoutAmount ?? escrowPayoutAmount(bet.totalPot);
      try {
        await creditWalletPayout(payout, 'Challenge earnings', bet.id);
        await refreshProfileAndWallet();
      } catch (err) {
        console.warn('creditWalletPayout:', err);
        setWallet((prev) => applyBetSettlementToState(prev, bet, resolution, uid));
      }
    } else if (!firebaseUser?.uid) {
      setWallet((prev) => applyBetSettlementToState(prev, bet, resolution, uid));
    }

    if (firebaseUser?.uid && bet.participants.length >= 2) {
      const [p1, p2] = bet.participants;
      recordMatchTransaction({
        matchId: bet.id,
        player1Id: p1.id,
        player2Id: p2.id,
        totalPot: resolution.totalPot ?? bet.totalPot ?? 0,
        platformFee: resolution.platformFee ?? 0,
        payoutAmount: resolution.payoutAmount ?? 0,
        winnerId: resolution.winningPlayerId ?? null,
      }).catch((err) => console.warn('recordMatchTransaction:', err));
    }
  }, [firebaseUser?.uid, refreshProfileAndWallet]);

  const useCloudChallenges = Boolean(firebaseUser?.uid);
  const useCloudSocial = Boolean(firebaseUser?.uid);
  const currentChatUid = firebaseUser?.uid ?? null;
  const unreadMessageCount = Object.values(unreadByFriendId).reduce((sum, n) => sum + n, 0);

  const accessCtx = useMemo(
    () => buildAccessContext({
      firebaseUid: firebaseUser?.uid ?? null,
      userId: firebaseUser?.uid ?? GUEST_USER_ID,
      useCloudFriends: useCloudSocial,
    }),
    [firebaseUser?.uid, useCloudSocial],
  );

  const showGuestRegistrationPrompt = useCallback(() => {
    setGuestPromptOpen(true);
  }, []);

  const ensureRegisteredForPlay = useCallback(() => {
    if (canCreateStandardChallenge(accessCtx)) return true;
    showGuestRegistrationPrompt();
    return false;
  }, [accessCtx, showGuestRegistrationPrompt]);

  const accessProfile = profileDoc;

  const hasLiveSubscription = hasLiveSubscriptionAccess(profileDoc);
  const subscriptionTier = getSubscriptionTier(profileDoc);
  const subscriptionStatus = profileDoc?.subscriptionStatus ?? null;
  const userCanSeeScoreStreaks = canSeeScoreStreaks(accessProfile);
  const userCanUseCustomWagers = canUseCustomWagers(accessProfile);
  const userCanUsePrivateChat = canUsePrivateChat(accessProfile);

  const { clearInboxStateForFriend } = usePrivateChatInbox({
    enabled: enableDataSubscriptions && useCloudSocial && userCanUsePrivateChat,
    currentUserId: currentChatUid,
    friends,
    openFriendId: privateChatFriend?.id,
    onUnreadChange: setUnreadByFriendId,
  });

  const ensureFriendSlot = () => {
    if (canAddFriendsUnlimited(accessProfile)) return true;
    if (canAddFreeFriend(countUserAddedFriends(friends))) return true;
    showToast(FREE_FRIEND_LIMIT_MESSAGE, 'critical');
    return false;
  };

  const hasRealProfile = profileIsComplete(profileDoc);

  useEffect(() => {
    if (!entered || !firebaseUser?.uid || profileDoc === undefined) return;
    if (hasRealProfile || isCreateProfileRoute || isAdminRoute(location.pathname)) return;
    if (location.pathname.startsWith('/join/') || location.pathname.startsWith('/ticket/')) return;
    navigate('/create-profile', { replace: true });
  }, [entered, firebaseUser?.uid, profileDoc, hasRealProfile, location.pathname, navigate, isCreateProfileRoute]);

  useEffect(() => {
    const pendingCode = peekPendingTicketCode();

    if (location.pathname.startsWith('/ticket/')) {
      return;
    }

    if (!firebaseUser?.uid) {
      if (pendingCode) logTicketResume('auth required', pendingCode);
      return;
    }

    if (!hasRealProfile) {
      if (pendingCode) logTicketResume('profile required', pendingCode);
      return;
    }

    if (pendingCode) {
      logTicketResume('resuming after profile', pendingCode);
      navigate(buildPendingTicketResumePath(pendingCode), { replace: true });
      return;
    }

    const token = peekPendingJoinToken();
    if (!token || location.pathname.startsWith('/join/')) return;
    navigate(`/join/${encodeURIComponent(token)}`, { replace: true });
  }, [hasRealProfile, firebaseUser?.uid, location.pathname, navigate]);

  usePushNotifications({
    enabled: Boolean(firebaseUser?.uid) && hasRealProfile && !showTour,
    uid: firebaseUser?.uid ?? null,
  });

  // Entering the app (/app and aliases) activates the app shell ("entered").
  // This covers fresh navigations, refreshes, and installed-PWA launches.
  // Guests enter directly; signed-in users without a profile are forwarded to
  // /create-profile by the redirect effect below.
  useEffect(() => {
    if (!onAppEntryRoute || entered) return;
    if (peekPendingTicketCode()) return;
    setEntered(true);
  }, [onAppEntryRoute, entered]);

  // The public marketing site must never run the app shell or its onboarding.
  // If we land on a public route while flagged as "entered" (e.g. stale session
  // flag), drop the flag so the marketing pages render cleanly.
  useEffect(() => {
    if (onPublicSiteRoute && entered) setEntered(false);
  }, [onPublicSiteRoute, entered]);

  // Auto-show welcome ticket for guest sessions (covers Enter as Guest).
  useEffect(() => {
    if (!entered || !authReady) return;
    if (!onAppEntryRoute) return;
    if (firebaseUser?.uid) {
      setShowGuestWelcome(false);
      return;
    }
    if (isGuestWelcomeTicketDismissed()) return;
    const timer = window.setTimeout(() => {
      if (
        enteredRef.current
        && !firebaseUserRef.current?.uid
        && !isGuestWelcomeTicketDismissed()
      ) {
        setShowGuestWelcome(true);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [entered, authReady, onAppEntryRoute, firebaseUser?.uid]);

  const myRiskTally =
    typeof profileDoc?.riskTally === 'number' ? profileDoc.riskTally : 0;

  const riskTallyByUserId = useMemo(() => {
    const map = {};
    if (firebaseUser?.uid) map[firebaseUser.uid] = myRiskTally;
    friends.forEach((f) => {
      if (f.id) map[f.id] = f.riskTally ?? 0;
    });
    return map;
  }, [firebaseUser?.uid, myRiskTally, friends]);

  const isAuthenticated = Boolean(firebaseUser?.uid);
  const isGuestSession = !isAuthenticated;
  const walletFields = mapWalletToUserFields(wallet);

  const tourHasFunds = (walletFields?.walletBalance ?? 0) >= MIN_TOUR_FUNDS;
  const tourIsPlus = hasLiveSubscription;
  const tourSteps = useMemo(
    () => buildGuidedTourSteps({
      isGuest: isGuestSession,
      hasFunds: tourHasFunds,
      isPlus: tourIsPlus,
    }),
    [isGuestSession, tourHasFunds, tourIsPlus],
  );

  const completeOnboardingTour = useCallback(async () => {
    setShowTour(false);
    justCreatedProfileRef.current = false;
    if (isGuestSession) {
      markGuestOnboardingSeen();
      console.log('[onboarding] guest seen saved');
      return;
    }
    if (!firebaseUser?.uid) return;
    // Local fallback first so completion sticks even if the Firestore write fails.
    markOnboardingCompletedLocal(firebaseUser.uid);
    if (profileDoc?.onboardingCompleted) {
      console.log('[onboarding] completed saved', { uid: firebaseUser.uid, source: 'already-set' });
      return;
    }
    try {
      const patch = await markOnboardingCompleted(firebaseUser.uid);
      if (patch) {
        setProfileDoc((prev) => mergeUserProfile(prev, patch));
      }
      console.log('[onboarding] completed saved', { uid: firebaseUser.uid, source: 'firestore' });
    } catch (err) {
      console.warn('[onboarding] could not persist completion (local fallback kept)', err?.message || err);
      console.log('[onboarding] completed saved', { uid: firebaseUser.uid, source: 'local-fallback' });
    }
  }, [firebaseUser?.uid, isGuestSession, profileDoc?.onboardingCompleted]);

  const openTourReplay = useCallback(() => {
    setShowHowToPlay(false);
    setShowGuestWelcome(false);
    setShowTour(true);
  }, []);

  const openHowToPlay = useCallback(() => {
    if (showTour) return;
    setShowHowToPlay(true);
  }, [showTour]);

  const dismissGuestWelcome = useCallback(() => {
    persistGuestWelcomeTicketDismissed();
    setShowGuestWelcome(false);
    if (shouldChainTourAfterGuestWelcomeDismiss({ isGuest: isGuestSession })) {
      window.setTimeout(() => setShowTour(true), 400);
    }
  }, [isGuestSession]);

  const profileDocLoaded = profileDoc !== undefined;

  // Debug: auth resolution (log once per auth-ready transition).
  const authResolvedLoggedRef = useRef(false);
  useEffect(() => {
    if (authReady && !authResolvedLoggedRef.current) {
      authResolvedLoggedRef.current = true;
      console.log('[onboarding] auth resolved', { uid: firebaseUser?.uid || null });
    }
  }, [authReady, firebaseUser?.uid]);

  // Debug: user doc loaded (log once per uid).
  const userDocLoggedRef = useRef(null);
  useEffect(() => {
    if (!firebaseUser?.uid || !profileDocLoaded) return;
    if (userDocLoggedRef.current === firebaseUser.uid) return;
    userDocLoggedRef.current = firebaseUser.uid;
    console.log('[onboarding] user doc loaded', {
      uid: firebaseUser.uid,
      onboardingCompleted: Boolean(profileDoc?.onboardingCompleted),
    });
  }, [firebaseUser?.uid, profileDocLoaded, profileDoc?.onboardingCompleted]);

  // Auto-show eligibility — tied to the user's Firestore profile, not just local state.
  //  - Guest: once per browser (lockedin_guest_onboarding_seen)
  //  - New registered user: once, right after first profile creation this session
  //  - Returning / completed / re-login users: never auto-show
  // Only runs once auth is resolved, the user doc is loaded, the shell is mounted,
  // and we're not on a ticket route.
  const eligibilityLoggedRef = useRef(null);
  useEffect(() => {
    if (!entered || !authReady) return undefined;
    if (!onAppEntryRoute) return undefined;
    if (location.pathname.startsWith('/ticket/')) return undefined;

    let eligible = false;
    let decision = null;

    if (isGuestSession) {
      if (!isOnboardingCompleted(null, { isGuest: true })) {
        eligible = true;
        decision = 'guest';
      }
    } else if (firebaseUser?.uid && profileDocLoaded && hasRealProfile) {
      const alreadyDone = isOnboardingCompleted(profileDoc, {
        isGuest: false,
        uid: firebaseUser.uid,
      });
      if (alreadyDone || !justCreatedProfileRef.current) {
        decision = `skip:${firebaseUser.uid}`;
        if (eligibilityLoggedRef.current !== decision) {
          eligibilityLoggedRef.current = decision;
          console.log('[onboarding] skipped returning user', { uid: firebaseUser.uid });
        }
        return undefined;
      }
      eligible = true;
      decision = `new:${firebaseUser.uid}`;
    }

    if (!eligible) return undefined;

    if (shouldDeferTourAutoShow({ isGuest: isGuestSession })) {
      return undefined;
    }

    if (eligibilityLoggedRef.current !== decision) {
      eligibilityLoggedRef.current = decision;
      console.log('[onboarding] eligible new user', {
        uid: firebaseUser?.uid || null,
        guest: isGuestSession,
      });
    }

    const timer = window.setTimeout(() => setShowTour(true), 900);
    return () => window.clearTimeout(timer);
  }, [
    entered,
    authReady,
    onAppEntryRoute,
    isGuestSession,
    firebaseUser?.uid,
    hasRealProfile,
    profileDocLoaded,
    profileDoc,
    location.pathname,
  ]);

  const userWithFriends = isGuestSession
      ? {
          ...guestUser,
          walletBalance: 0,
          walletDisabled: false,
          friends: [],
          avatar: undefined,
          riskTally: 0,
        }
      : {
          id: firebaseUser.uid,
          name: profileDoc?.displayName || profileDoc?.usernameLower || 'Player',
          username: profileDoc?.displayName || profileDoc?.usernameLower || 'Player',
          favoriteSport: profileDoc?.favoriteSport || null,
          ...walletFields,
          friends,
          avatar: userAvatar ?? profileDoc?.avatarUrl ?? undefined,
          riskTally: myRiskTally,
        };
  const isSuspended = userStrikes >= 3
    && suspensionEndsAt != null
    && Date.now() < suspensionEndsAt;
  const isAccountLocked = accountLockEndsAt != null
    && Date.now() < accountLockEndsAt;

  const ensureAccountUnlocked = () => {
    if (!isAccountLocked) return true;
    showToast(`Custom challenges are disabled for 24 hours after an unresolved dispute (until ${new Date(accountLockEndsAt).toLocaleString()}). Live challenges and the rest of The Lockbox still work.`)
    return false;
  };

  const LOCKEDIN_BETS_KEY = 'lockedin_bets';

  const isLegacyMockBet = (bet) =>
    LEGACY_MOCK_BET_IDS.has(bet?.id)
    || LEGACY_MOCK_USER_IDS.has(bet?.creatorId)
    || LEGACY_MOCK_USER_IDS.has(bet?.recipientId);

  const loadSavedBets = () => {
    try {
      const raw = window.localStorage.getItem(LOCKEDIN_BETS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const cleaned = parsed.filter((bet) => !isLegacyMockBet(bet));
      if (cleaned.length !== parsed.length) {
        window.localStorage.setItem(LOCKEDIN_BETS_KEY, JSON.stringify(cleaned));
      }
      return cleaned;
    } catch (_) {}
    return [];
  };

  const [mockBets, setMockBets] = useState(() => (firebaseUser?.uid ? [] : loadSavedBets()));

  const showWinnerNotification = (resolution, { released = false, autoReleased = false } = {}) => {
    setNotification({
      show: true,
      winner: resolution,
      message: formatWinnerNotificationMessage(resolution, { released, autoReleased }),
      autoReleased: !!autoReleased,
    });
  };

  const showLoserLiveNotification = (resolution) => {
    emitNotification({
      type: NotificationType.SYSTEM,
      title: 'Game final',
      body: `${formatLoserLiveNotificationMessage(resolution)} ${COMPLETED_VIEW_IN_ARENA}`,
      priority: NotificationPriority.HIGH,
      dedupeKey: `game_final_${resolution?.betId || 'live'}`,
      meta: { navigate: MY_ARENA_COMPLETED_NAV, challengeId: resolution?.betId ?? null },
    });
  };

  const notifyLiveGameSettlement = useCallback((resolution, bet = null) => {
    const uid = userWithFriends.id;
    if (resolution.winningPlayerId === uid) {
      showWinnerNotification(resolution, { released: true, autoReleased: true });
    } else if (bet && resolution.isDraw) {
      notifyCompletedTransition(LifecycleTransition.SETTLED_DRAW, bet, { viewerId: uid });
    } else if (bet) {
      notifyCompletedTransition(LifecycleTransition.SETTLED_LOST, bet, { viewerId: uid });
    } else {
      showLoserLiveNotification(resolution);
    }
  }, [userWithFriends.id, notifyCompletedTransition]);

  useEffect(() => {
    if (useCloudChallenges) return undefined;
    try {
      window.localStorage.setItem(LOCKEDIN_BETS_KEY, JSON.stringify(mockBets));
    } catch (_) {}
    return undefined;
  }, [mockBets, useCloudChallenges]);

  const resetChallengeSessionState = useCallback(() => {
    setMockBets([]);
    challengesHydratedRef.current = false;
    finalOutcomeNotifiedRef.current = new Set();
    settlementNotifiedRef.current = new Set();
    declinedChallengeNotifiedRef.current = new Set();
    statsRecordedBetIdsRef.current = new Set();
    try {
      window.localStorage.removeItem(LOCKEDIN_BETS_KEY);
    } catch (_) {}
  }, []);

  const resetGuestSensitiveState = useCallback((previousUid) => {
    clearGuestSensitiveLocalStorage(previousUid);
    setUserAvatar(null);
    setProfileDoc(undefined);
    setWallet(createDefaultWalletState());
    resetChallengeSessionState();
  }, [resetChallengeSessionState]);

  const prevAuthUidRef = useRef(null);
  const authUidInitializedRef = useRef(false);
  useEffect(() => {
    if (!authPerfLoggedRef.current) return;
    const uid = firebaseUser?.uid ?? null;
    const prevUid = prevAuthUidRef.current;
    if (!authUidInitializedRef.current) {
      authUidInitializedRef.current = true;
      prevAuthUidRef.current = uid;
      return;
    }
    if (prevUid !== uid) {
      clearGuestSensitiveLocalStorage(prevUid);
      resetChallengeSessionState();
      prevAuthUidRef.current = uid;
    }
  }, [firebaseUser?.uid, resetChallengeSessionState]);

  const guestDeepLinkResetRef = useRef(false);
  useEffect(() => {
    if (guestDeepLinkResetRef.current || !entered) return;
    if (!authReady) return;
    if (firebaseUser?.uid) return;
    guestDeepLinkResetRef.current = true;
    resetGuestSensitiveState(null);
  }, [entered, authReady, firebaseUser?.uid, resetGuestSensitiveState]);

  useEffect(() => {
    const onPopState = () => {
      if (!enteredRef.current) return;
      if (closeTopModal()) {
        try {
          window.history.pushState({ lockbox: true }, '', window.location.href);
        } catch (_) {}
        return;
      }
      const wasGuest = !firebaseUserRef.current?.uid;
      setEntered(false);
      setShowHowToPlay(false);
      setShowGuestWelcome(false);
      setProfileOpen(false);
      setPrivateChatFriend(null);
      if (wasGuest) {
        resetGuestSensitiveState(null);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [resetGuestSensitiveState]);

  useEffect(() => {
    if (!enableDataSubscriptions || !useCloudChallenges) return undefined;
    challengesHydratedRef.current = false;
    finalOutcomeNotifiedRef.current = new Set();
    settlementNotifiedRef.current = new Set();
    declinedChallengeNotifiedRef.current = new Set();
    statsRecordedBetIdsRef.current = new Set();
    const unsub = subscribeUserChallenges(
      firebaseUser.uid,
      (bets) => {
        setMockBets(bets);
        setPlayerStats(computePlayerStatsFromSettledBets(bets));
        setPlayerStatsReady(true);
        const uid = firebaseUser.uid;
        if (!challengesHydratedRef.current) {
          challengesHydratedRef.current = true;
          bets.forEach((b) => {
            if (b.finalOutcome) finalOutcomeNotifiedRef.current.add(`${b.id}-final`);
            if (b.settlement) settlementNotifiedRef.current.add(`${b.id}-settled`);
            if (b.status === ChallengeStatus.DECLINED && b.creatorId === uid) {
              declinedChallengeNotifiedRef.current.add(b.id);
            }
            if (
              b.status === ChallengeStatus.DECLINED
              && b.recipientId === uid
              && b.declinedByUserId === b.creatorId
            ) {
              declinedChallengeNotifiedRef.current.add(b.id);
            }
          });
          return;
        }

        bets.forEach((bet) => {
          const isLive = !bet.matchup?.isCustom;
          if (
            bet.finalOutcome
            && !bet.settlement
            && !finalOutcomeNotifiedRef.current.has(`${bet.id}-final`)
            && bet.matchup?.isCustom
          ) {
            finalOutcomeNotifiedRef.current.add(`${bet.id}-final`);
            showWinnerNotification(bet.finalOutcome, { released: false });
          }
          if (
            bet.settlement
            && !settlementNotifiedRef.current.has(`${bet.id}-settled`)
          ) {
            settlementNotifiedRef.current.add(`${bet.id}-settled`);
            finalOutcomeNotifiedRef.current.add(`${bet.id}-final`);
            recordBetResult(bet, bet.settlement);
            const uid = firebaseUser.uid;
            if (isLive) {
              notifyLiveGameSettlement(bet.settlement, bet);
            } else {
              if (bet.settlement.winningPlayerId === uid) {
                showWinnerNotification(bet.settlement, { released: true });
              } else if (bet.settlement.isDraw) {
                notifyCompletedTransition(LifecycleTransition.SETTLED_DRAW, bet, { viewerId: uid });
              } else {
                notifyCompletedTransition(LifecycleTransition.SETTLED_LOST, bet, { viewerId: uid });
              }
            }
          }
          const declinedByRecipient =
            bet.status === ChallengeStatus.DECLINED
            && bet.creatorId === uid
            && bet.declinedByUserId === bet.recipientId;
          if (declinedByRecipient && !declinedChallengeNotifiedRef.current.has(bet.id)) {
            declinedChallengeNotifiedRef.current.add(bet.id);
            if (bet.creatorId === uid) {
              const stake = bet.participants?.find((p) => p.id === uid)?.amount;
              if (stake) {
                void refundStakeToWallet(stake, bet.id, 'Challenge declined refund');
              }
            }
            notifyCompletedTransition(
              LifecycleTransition.DECLINED_BY_RECIPIENT,
              bet,
              { viewerId: uid },
            );
          }
          const declinedBySender =
            bet.status === ChallengeStatus.DECLINED
            && bet.recipientId === uid
            && bet.declinedByUserId === bet.creatorId;
          if (declinedBySender && !declinedChallengeNotifiedRef.current.has(bet.id)) {
            declinedChallengeNotifiedRef.current.add(bet.id);
            notifyCompletedTransition(
              LifecycleTransition.DECLINED_BY_SENDER,
              bet,
              { viewerId: uid },
            );
          }
        });
      },
      () => {
        showToast('Could not load challenges. Check Firestore rules and emulator.');
      }
    );
    return unsub;
  }, [enableDataSubscriptions, useCloudChallenges, firebaseUser?.uid, recordBetResult, notifyLiveGameSettlement, refundStakeToWallet, notifyCompletedTransition]);

  const clearSavedSession = () => {
    try {
      window.localStorage.removeItem(LOCKEDIN_BETS_KEY);
      purgeLegacyChatLocalStorage();
    } catch (_) {}
  };

  const handleLeaveApp = async () => {
    const previousUid = firebaseUser?.uid ?? null;
    clearSavedSession();
    resetGuestSensitiveState(previousUid);
    try {
      await firebaseSignOut(auth);
    } catch (_) {}
    setEntered(false);
    setShowGuestWelcome(false);
    setProfileOpen(false);
    navigate('/');
  };

  const settleLiveGameWager = async (betId, bet, resolution, mergedMatchup) => {
    if (bet.matchup?.isCustom || settlementNotifiedRef.current.has(`${betId}-settled`)) {
      return;
    }

    const settlement = {
      ...stripClientFeeFields(resolution),
      kind: 'funds_released',
      autoReleased: true,
    };
    const settledBet = { ...bet, matchup: mergedMatchup, finalOutcome: resolution, settlement };

    if (useCloudChallenges) {
      try {
        const settled = await settleChallengeOnServer({
          matchId: betId,
          resolution: settlement,
          matchup: mergedMatchup,
        });
        if (settled?.settlement) {
          settledBet.settlement = settled.settlement;
          if (settled.finalOutcome) settledBet.finalOutcome = settled.finalOutcome;
        }
        await refreshProfileAndWallet();
      } catch (e) {
        console.error(e);
        return;
      }
    } else {
      setMockBets((prev) => prev.filter((b) => b.id !== betId));
    }

    settlementNotifiedRef.current.add(`${betId}-settled`);
    finalOutcomeNotifiedRef.current.add(`${betId}-final`);
    recordBetResult(settledBet, settlement);
    notifyLiveGameSettlement(settlement, settledBet);
  };

  const handleReportGameFinal = async (betId, resolution, matchupPatch) => {
    const bet = mockBets.find((b) => b.id === betId);
    if (!bet || bet.finalOutcome || bet.settlement) return;

    const mergedMatchup = matchupPatch
      ? { ...bet.matchup, ...matchupPatch }
      : bet.matchup;

    if (!bet.matchup?.isCustom) {
      await settleLiveGameWager(betId, bet, resolution, mergedMatchup);
      return;
    }

    if (useCloudChallenges) {
      try {
        await recordChallengeFinalOutcome(betId, stripClientFeeFields(resolution), mergedMatchup);
      } catch (e) {
        console.error(e);
      }
    } else {
      setMockBets((prev) =>
        prev.map((b) =>
          (b.id === betId
            ? { ...b, matchup: mergedMatchup, finalOutcome: resolution }
            : b)
        )
      );
    }
    if (!finalOutcomeNotifiedRef.current.has(`${betId}-final`)) {
      finalOutcomeNotifiedRef.current.add(`${betId}-final`);
      showWinnerNotification(resolution, { released: false });
    }
  };

  const ESCROW_LOCKED_STATUSES = new Set([
    ChallengeStatus.ACTIVE,
    ChallengeStatus.LOCKED_IN,
    ChallengeStatus.PENDING_WINNER,
    ChallengeStatus.DISPUTED,
  ]);

  const handleClaimWinnings = async (betId) => {
    if (!ensureRegisteredForPlay()) return false;
    if (!ensureAccountUnlocked()) return false;
    const bet = mockBets.find((b) => b.id === betId);
    if (!bet?.finalOutcome?.winningPlayerId) {
      showToast('Winner has not been determined yet. Funds remain in locked funds.')
      return false;
    }
    if (bet.finalOutcome.winningPlayerId !== userWithFriends.id) {
      showToast(`Only ${bet.finalOutcome.winningPlayerName || 'the winner'} can claim challenge earnings and release funds to their wallet.`)
      return false;
    }
    return handleResolve(betId, bet.finalOutcome);
  };

  const handleResolve = async (betId, winnerOrResolution) => {
    if (!ensureAccountUnlocked()) return false;
    const bet = mockBets.find((b) => b.id === betId);
    if (!bet) return false;
    let resolution =
      winnerOrResolution?.winningTeam
        ? winnerOrResolution
        : buildResolutionFromBet(bet, winnerOrResolution, 'funds_released');

    if (resolution.winningPlayerId && resolution.winningPlayerId !== userWithFriends.id) {
      showToast('Only the winner can accept and release locked funds to their wallet.')
      return false;
    }

    if (useCloudChallenges) {
      try {
        const settled = await settleChallengeOnServer({
          matchId: betId,
          resolution: stripClientFeeFields(resolution),
        });
        if (settled?.settlement) {
          resolution = settled.settlement;
        }
        await refreshProfileAndWallet();
      } catch (e) {
        console.error(e);
        return false;
      }
    } else {
      setMockBets((prev) => prev.filter((b) => b.id !== betId));
    }
    settlementNotifiedRef.current.add(`${betId}-settled`);
    recordBetResult(bet, resolution);
    showWinnerNotification(resolution, { released: true });
    return true;
  };

  const handleUpdateBetMatchup = async (betId, updates) => {
    if (useCloudChallenges) {
      const bet = mockBets.find((b) => b.id === betId);
      if (bet) {
        try {
          await updateChallenge(betId, { matchup: { ...bet.matchup, ...updates } });
        } catch (e) {
          console.error(e);
        }
      }
      return;
    }
    setMockBets((prev) =>
      prev.map((b) => (b.id === betId ? { ...b, matchup: { ...b.matchup, ...updates } } : b))
    );
  };

  const handleAccept = async (betId) => {
    if (!ensureRegisteredForPlay()) return;
    const bet = mockBets.find((b) => b.id === betId);
    const myStake = bet?.participants?.find((p) => p.id === userWithFriends.id)?.amount ?? 0;
    if (!validateStakeForPlay(myStake)) return;

    if (!(await deductStakeForPlay(myStake, betId, 'Challenge accept entry'))) return;

    if (useCloudChallenges) {
      try {
        await acceptChallenge(betId);
      } catch (e) {
        console.error(e);
        await refundStakeToWallet(myStake, betId, 'Challenge accept rollback');
        showToast('Could not accept challenge. Try again.')
        return;
      }
    } else {
      setMockBets((prev) => prev.map((b) => (b.id === betId ? { ...b, status: 'ACTIVE' } : b)));
    }
    logChallengeLifecycle({
      challengeId: betId,
      oldStatus: bet?.status ?? ChallengeStatus.PENDING_ACCEPTANCE,
      newStatus: ChallengeStatus.ACTIVE,
      reason: 'recipient_accepted',
      userId: userWithFriends.id,
      source: 'App.handleAccept',
    });
    showToast('Challenge accepted. Both entries are moved to locked funds.')
  };

  const handleDecline = async (betId) => {
    const bet = mockBets.find((b) => b.id === betId);
    if (bet && ESCROW_LOCKED_STATUSES.has(bet.status)) {
      showToast('Locked funds are active. You cannot opt out after the challenge has been agreed.')
      return;
    }
    const iAmRecipient = bet?.recipientId === userWithFriends.id;
    if (useCloudChallenges) {
      try {
        await declineChallenge(betId, userWithFriends.id);
      } catch (e) {
        console.error(e);
        showToast('Could not decline challenge. Try again.')
        return;
      }
    } else {
      setMockBets((prev) => prev.filter((b) => b.id !== betId));
    }
    if (bet && !iAmRecipient) {
      refundCreatorStakeForBet(bet);
    }
    logChallengeLifecycle({
      challengeId: betId,
      oldStatus: bet?.status ?? ChallengeStatus.PENDING_ACCEPTANCE,
      newStatus: ChallengeStatus.DECLINED,
      reason: iAmRecipient ? 'recipient_declined' : 'sender_cancelled',
      userId: userWithFriends.id,
      source: 'App.handleDecline',
    });
    showToast(iAmRecipient ? 'Challenge declined.' : 'Challenge canceled.')
  };

  const handleCounter = async (betId, amount) => {
    if (!ensureRegisteredForPlay()) return;
    const bet = mockBets.find((b) => b.id === betId);
    if (!bet) return;
    const stake = Number(amount);
    if (!validateStakeForPlay(stake)) return;
    const updatedParticipants = bet.participants.map((p, i) =>
      (i === 0 ? { ...p, amount: stake } : { ...p, amount: stake })
    );
    const counterTargetId =
      bet.creatorId === userWithFriends.id ? bet.recipientId : bet.creatorId;
    if (useCloudChallenges) {
      try {
        await counterChallenge(betId, counterTargetId, stake, updatedParticipants);
      } catch (e) {
        console.error(e);
        showToast('Could not send counter offer.')
        return;
      }
    } else {
      setMockBets((prev) =>
        prev.map((b) =>
          b.id === betId
            ? {
                ...b,
                participants: updatedParticipants,
                totalPot: stake * 2,
                waitingForAmountApprovalFromId: counterTargetId,
              }
            : b
        )
      );
    }
    showToast(`Counter offer sent: $${stake} per person`, 'success');
  };

  const handleTabChange = (tab) => {
    console.log('Tab changed to:', tab);
  };

  const handleCreateCustomWager = async (newBet) => {
    if (!ensureRegisteredForPlay()) return;
    if (!canReceiveFriendRequest(firebaseUser?.uid)) {
      handleRequireRegistration();
      return;
    }
    if (!userCanUseCustomWagers) {
      showToast(SUBSCRIPTION_REQUIRED_MESSAGE)
      return;
    }
    if (!ensureAccountUnlocked()) return;
    const creatorId = firebaseUser?.uid || newBet?.creatorId;
    const recipientId = newBet?.recipientId;
    if (!creatorId || !recipientId) {
      showToast('Sign in and select an opponent to send a custom challenge.')
      return;
    }
    const opponent = friends.find((f) => f.id === recipientId);
    if (!opponent) {
      showToast('You can only send custom challenges to friends on your squad.',)
      return;
    }
    if (isMessagingBlockedWithPlayer(recipientId, friends)) {
      showToast(opponent.blockedByMe
          ? 'You blocked this player. Unblock them in Friends to challenge them.'
          : 'You cannot challenge this player while they have you blocked.',)
      return;
    }
    const creatorStake =
      newBet.participants?.find((p) => p.id === creatorId)?.amount
      ?? newBet.participants?.find((p) => p.id === newBet.creatorId)?.amount
      ?? 0;

    const betPayload = {
      ...newBet,
      creatorId,
      recipientId,
      participants: (newBet.participants || []).map((p) =>
        p.id === newBet.creatorId ? { ...p, id: creatorId } : p
      ),
      agreedBy: (newBet.agreedBy || []).map((id) => (id === newBet.creatorId ? creatorId : id)),
    };

    if (useCloudChallenges) {
      const challengeRef = buildChallengeRef();
      const challengeId = challengeRef.id;
      betPayload.id = challengeId;

      try {
        const friendsOk = await areFriends(creatorId, recipientId);
        if (!friendsOk) {
          showToast('You must be friends with this player before sending a custom challenge. Re-accept the friend request if needed.',)
          return;
        }
        const [creatorProfile, recipientProfile] = await Promise.all([
          fetchUserProfile(creatorId),
          fetchUserProfile(recipientId),
        ]);

        // Custom Challenges require BOTH members to be Plus. Block before
        // sending to a non-Plus recipient and surface an upgrade moment
        // instead of letting the server reject it later.
        if (!canSendChallenge(profileDoc, 'custom', recipientProfile)) {
          const reason = customSendBlockReason(profileDoc, 'custom', recipientProfile);
          showToast(getCustomSendUpgradeCopy(reason).body);
          return;
        }

        const creatorRisk = creatorProfile?.riskTally ?? 0;
        const recipientRisk = recipientProfile?.riskTally ?? 0;

        const participants = (betPayload.participants || []).map((p) => ({
          ...p,
          riskTally:
            p.id === creatorId
              ? creatorRisk
              : p.id === recipientId
                ? recipientRisk
                : p.riskTally ?? 0,
        }));

        await createChallenge({
          ...betPayload,
          challengeType: 'custom',
          participants,
          creatorRiskTally: creatorRisk,
          recipientRiskTally: recipientRisk,
        }, challengeRef);

        if (!(await deductStakeForPlay(creatorStake, challengeId, 'Custom challenge entry'))) {
          await removeChallenge(challengeId);
          return;
        }
      } catch (e) {
        console.error(e);
        try {
          await removeChallenge(betPayload.id);
        } catch (_) { /* doc may not exist */ }
        showToast(getChallengeSendErrorMessage(e, { isCustom: true }));
        return;
      }
    } else {
      if (!(await deductStakeForPlay(creatorStake, betPayload.id, 'Custom challenge entry'))) return;
      setMockBets((prev) => [
        ...prev,
        {
          ...betPayload,
          creatorRiskTally: newBet.creatorRiskTally ?? 0,
          recipientRiskTally: newBet.recipientRiskTally ?? 0,
          participants: (betPayload.participants || []).map((p) => ({ ...p, riskTally: p.riskTally ?? 0 })),
        },
      ]);
    }
    showToast(
      `Challenge sent to ${opponent.name || 'opponent'}. They'll be notified to agree or decline.`,
      'success'
    );
  };

  const handleCreateOpenChallenge = async (payload) => {
    if (!ensureRegisteredForPlay()) return;
    if (!canReceiveFriendRequest(firebaseUser?.uid)) {
      handleRequireRegistration();
      return;
    }
    if (!userCanUseCustomWagers) {
      showToast(SUBSCRIPTION_REQUIRED_MESSAGE);
      return;
    }
    if (!ensureAccountUnlocked()) return;
    const creatorId = firebaseUser?.uid || payload?.creatorId;
    if (!creatorId || !useCloudChallenges) {
      handleRequireRegistration();
      return;
    }
    const stake = Number(payload.stake);
    if (!stake || stake <= 0) {
      showToast('Enter a valid challenge amount.');
      return;
    }

    const ref = buildOpenChallengeRef();
    const openId = ref.id;

    try {
      const groupId = payload.groupId?.trim?.() || payload.groupId || null;
      await writeOpenChallenge(ref, {
        creatorId,
        creatorUsername: payload.creatorUsername || userWithFriends.name || 'Player',
        wager: payload.wager,
        creatorSide: payload.creatorSide,
        opponentSide: payload.opponentSide,
        stake,
        sport: payload.sport ?? null,
        gameId: payload.gameId ?? null,
        groupId,
        groupName: groupId ? (payload.groupName || null) : null,
      });

      if (!(await deductStakeForPlay(stake, openId, groupId ? 'Group lock entry' : 'Open arena entry'))) {
        await cancelOpenChallenge(openId, creatorId);
        return;
      }

      showToast(
        groupId
          ? 'Posted to your group. Waiting for a member to accept.'
          : 'Posted to Open Arena. Waiting for an opponent.',
        'success'
      );
      completeOnboardingTour();
    } catch (e) {
      console.error(e);
      try {
        await cancelOpenChallenge(openId, creatorId);
      } catch (_) { /* may not exist if write failed */ }
      showToast(getOpenChallengePostErrorMessage(e));
    }
  };

  const handleAcceptOpenChallenge = async (openChallenge) => {
    if (!ensureRegisteredForPlay()) return;
    if (!userCanUseCustomWagers) {
      showToast(SUBSCRIPTION_REQUIRED_MESSAGE);
      return;
    }
    if (!ensureAccountUnlocked()) return;
    const acceptorId = firebaseUser?.uid;
    if (!acceptorId || !useCloudChallenges) {
      showToast('Sign in to accept open challenges.');
      return;
    }
    if (openChallenge.creatorId === acceptorId) {
      showToast('You cannot accept your own challenge.');
      return;
    }
    const stake = Number(openChallenge.stake);
    if (!validateStakeForPlay(stake)) return;

    const challengeRef = doc(collection(db, CHALLENGES_COLLECTION));
    const challengeId = challengeRef.id;

    try {
      const [creatorProfile, acceptorProfile] = await Promise.all([
        fetchUserProfile(openChallenge.creatorId),
        fetchUserProfile(acceptorId),
      ]);

      await acceptOpenChallengeTransaction({
        openChallengeId: openChallenge.id,
        acceptorId,
        acceptorName: userWithFriends.name || 'Player',
        acceptorRiskTally: acceptorProfile?.riskTally ?? 0,
        creatorRiskTally: creatorProfile?.riskTally ?? 0,
        challengeRef,
      });
    } catch (e) {
      console.error(e);
      const msg = e?.message || '';
      if (msg === 'CHALLENGE_NOT_OPEN' || msg === 'CHALLENGE_NOT_FOUND') {
        showToast('This challenge was already taken or removed.');
      } else if (msg === 'CANNOT_ACCEPT_OWN') {
        showToast('You cannot accept your own challenge.');
      } else if (msg === 'CHALLENGE_EXPIRED') {
        showToast('This challenge has expired.');
      } else {
        showToast('Could not accept challenge. Try again.');
      }
      return;
    }

    if (!(await deductStakeForPlay(stake, challengeId, 'Open arena accept entry'))) {
      try {
        await revertOpenChallengeAccept({
          openChallengeId: openChallenge.id,
          challengeId,
          actorId: acceptorId,
        });
      } catch (rollbackErr) {
        console.warn('open accept rollback:', rollbackErr);
        try {
          await removeChallenge(challengeId);
        } catch (_) {}
      }
      showToast('Could not lock your challenge funds. The open challenge is available again.');
      return;
    }

    showToast('Challenge accepted. Both entries are moved to locked funds.', 'success');
  };

  const handleCancelOpenChallenge = async (openChallengeId) => {
    const creatorId = firebaseUser?.uid;
    if (!creatorId || !useCloudChallenges) return;

    let stake = 0;
    try {
      const openSnap = await getDoc(doc(db, OPEN_CHALLENGES_COLLECTION, openChallengeId));
      if (openSnap.exists()) {
        stake = Number(openSnap.data().stake) || 0;
      }
      await cancelOpenChallenge(openChallengeId, creatorId);
      if (stake > 0) {
        await refundStakeToWallet(stake, openChallengeId, 'Open arena cancel refund');
      }
      showToast('Challenge removed from Open Arena.', 'success');
    } catch (e) {
      console.error(e);
      showToast('Could not cancel challenge. Try again.');
    }
  };

  const handleAgreeCustomWager = async (betId) => {
    if (!ensureRegisteredForPlay()) return;
    if (!userCanUseCustomWagers) {
      showToast(ENTITLEMENT_COPY.customAcceptNeedsPlus.body);
      return;
    }
    if (!ensureAccountUnlocked()) return;
    const bet = mockBets.find((b) => b.id === betId);
    const myStake = bet?.participants?.find((p) => p.id === userWithFriends.id)?.amount ?? 0;
    const iAmRecipient = bet?.recipientId === userWithFriends.id;
    const agreedBy = [...(bet?.agreedBy || []), userWithFriends.id].filter(
      (id, i, arr) => arr.indexOf(id) === i
    );
    const willLockIn = agreedBy.length >= 2;
    if (iAmRecipient && willLockIn && !validateStakeForPlay(myStake)) return;

    if (iAmRecipient && willLockIn) {
      if (!(await deductStakeForPlay(myStake, betId, 'Custom challenge agree entry'))) return;
    }

    if (useCloudChallenges && bet) {
      try {
        await agreeCustomChallenge(betId, userWithFriends.id, bet.agreedBy || []);
      } catch (e) {
        console.error(e);
        if (iAmRecipient && willLockIn) {
          await refundStakeToWallet(myStake, betId, 'Custom challenge agree rollback');
        }
        showToast('Could not agree to challenge.')
        return;
      }
    } else {
      setMockBets((prev) =>
        prev.map((b) => {
          if (b.id !== betId || b.status !== 'PENDING_AGREEMENT') return b;
          if (b.recipientId !== userWithFriends.id) return b;
          const agreedBy = [...(b.agreedBy || []), userWithFriends.id].filter(
            (id, i, arr) => arr.indexOf(id) === i
          );
          const bothAgreed = agreedBy.length >= 2;
          return {
            ...b,
            agreedBy,
            status: bothAgreed ? 'LOCKED_IN' : b.status,
          };
        })
      );
    }
    logChallengeLifecycle({
      challengeId: betId,
      oldStatus: ChallengeStatus.PENDING_AGREEMENT,
      newStatus: willLockIn ? ChallengeStatus.LOCKED_IN : ChallengeStatus.PENDING_AGREEMENT,
      reason: willLockIn ? 'both_agreed' : 'one_agreed',
      userId: userWithFriends.id,
      source: 'App.handleAgreeCustomWager',
    });
    showToast('Challenge accepted. Both entries are moved to locked funds.')
  };

  const handleDeclineCustomWager = async (betId) => {
    // Plus is required to ACCEPT a custom challenge, never to decline/cancel one.
    // A free recipient (or the Plus sender) must always be able to back out
    // while the challenge is still pre-lock (PENDING_AGREEMENT).
    const bet = mockBets.find((b) => b.id === betId);
    if (bet && bet.status !== ChallengeStatus.PENDING_AGREEMENT) {
      showToast('Locked funds are active. You cannot cancel after both players have agreed.')
      return;
    }
    if (useCloudChallenges) {
      try {
        await declineChallenge(betId, userWithFriends.id);
      } catch (e) {
        console.error(e);
        showToast('Could not decline challenge.')
        return;
      }
    } else {
      setMockBets((prev) => prev.filter((b) => b.id !== betId));
    }
    const iAmCreator = bet?.creatorId === userWithFriends.id;
    if (iAmCreator) {
      refundCreatorStakeForBet(bet);
    }
    logChallengeLifecycle({
      challengeId: betId,
      oldStatus: bet?.status ?? ChallengeStatus.PENDING_AGREEMENT,
      newStatus: ChallengeStatus.DECLINED,
      reason: iAmCreator ? 'sender_cancelled' : 'recipient_declined',
      userId: userWithFriends.id,
      source: 'App.handleDeclineCustomWager',
    });
    showToast(iAmCreator
          ? 'Challenge canceled. Your entry was returned to your wallet.'
          : 'Challenge declined.')
  };

  const applyCustomWagerDisputeEscalation = useCallback(
    async (bet, { winnerVotes } = {}) => {
      if (!bet?.id || secondChanceEscalatedBetIdsRef.current.has(bet.id)) return;
      secondChanceEscalatedBetIdsRef.current.add(bet.id);

      const disputeEndsAt = Date.now() + DISPUTE_LOCK_MS;
      const patch = {
        status: ChallengeStatus.DISPUTED,
        secondChanceEndsAt: null,
        disputeEndsAt,
        creatorRiskTally: (bet.creatorRiskTally ?? 0) + 1,
        recipientRiskTally: (bet.recipientRiskTally ?? 0) + 1,
        ...(winnerVotes ? { winnerVotes } : {}),
      };

      const uid = firebaseUser?.uid;

      if (useCloudChallenges) {
        await updateChallenge(bet.id, patch);
        if (
          uid
          && (bet.creatorId === uid || bet.recipientId === uid)
          && !riskTalliedBetIdsRef.current.has(riskTallyEscalationKey(bet.id, uid))
        ) {
          riskTalliedBetIdsRef.current.add(riskTallyEscalationKey(bet.id, uid));
          const penalty = await applyDisputePenaltyOnServer(disputeEndsAt);
          setAccountLockEndsAt((prev) => Math.max(prev || 0, disputeEndsAt));
          if (uid === firebaseUser?.uid) {
            setProfileDoc((prev) =>
              prev
                ? {
                    ...prev,
                    riskTally: penalty?.riskTally ?? (prev.riskTally ?? 0) + 1,
                    accountLockEndsAt: penalty?.accountLockEndsAt ?? disputeEndsAt,
                  }
                : prev
            );
          }
          setFriends((prev) =>
            prev.map((f) =>
              (f.id === uid ? { ...f, riskTally: (f.riskTally ?? 0) + 1 } : f)
            )
          );
        }
      } else {
        setAccountLockEndsAt(disputeEndsAt);
        setMockBets((prev) =>
          prev.map((b) => (b.id === bet.id ? { ...b, ...patch } : b))
        );
      }
    },
    [useCloudChallenges, firebaseUser?.uid]
  );

  const handleSubmitWinner = async (betId, winnerPick) => {
    if (!ensureAccountUnlocked()) return;
    const bet = mockBets.find((b) => b.id === betId);
    if (!bet) return;
    const winnerVotes = { ...(bet.winnerVotes || {}), [userWithFriends.id]: winnerPick };
    const ids = (bet.participants || []).map((p) => p.id);
    const voted = ids.filter((id) => winnerVotes[id]);
    const same = voted.length === 2 && winnerVotes[ids[0]] === winnerVotes[ids[1]];
    const mismatch = voted.length === 2 && winnerVotes[ids[0]] !== winnerVotes[ids[1]];

    if (same) {
      const resolution = buildResolutionFromCustomVote(bet, winnerPick);
      if (useCloudChallenges) {
        try {
          await updateChallenge(betId, {
            winnerVotes,
            finalOutcome: resolution,
            status: ChallengeStatus.LOCKED_IN,
            secondChanceEndsAt: null,
            disputeEndsAt: null,
          });
        } catch (e) {
          console.error(e);
        }
      } else {
        setMockBets((prev) =>
          prev.map((b) =>
            (b.id === betId
              ? {
                  ...b,
                  winnerVotes,
                  finalOutcome: resolution,
                  status: ChallengeStatus.LOCKED_IN,
                  secondChanceEndsAt: null,
                  disputeEndsAt: null,
                }
              : b)
          )
        );
      }
      const iWon = resolution.winningPlayerId === userWithFriends.id;
      showToast(iWon
          ? `You won the custom challenge! Use the Earnings ready alert to release $${resolution.payoutAmount?.toFixed(2)} to your wallet.`
          : `${resolution.winningPlayerName || 'Your opponent'} won. Locked funds stay until they claim challenge earnings.`)
      return;
    }
    if (mismatch) {
      const now = Date.now();

      // Second-chance round: both players must re-vote (votes were cleared on entry).
      if (bet.status === ChallengeStatus.SECOND_CHANCE) {
        try {
          await applyCustomWagerDisputeEscalation(bet, { winnerVotes });
        } catch (e) {
          console.error(e);
        }
        showToast(
          'Both players re-voted and still disagree. The 15-minute window is void — funds are locked for 24 hours, custom challenges are disabled with everyone, and risk tally is applied.',
          'critical'
        );
        return;
      }

      // First mismatch → second chance: clear votes so both must re-vote before any agreement.
      const secondChanceEndsAt = now + SECOND_CHANCE_MS;
      const patch = {
        winnerVotes: {},
        status: ChallengeStatus.SECOND_CHANCE,
        secondChanceEndsAt,
        disputeEndsAt: null,
      };
      if (useCloudChallenges) {
        try {
          await updateChallenge(betId, patch);
        } catch (e) {
          console.error(e);
        }
      } else {
        setMockBets((prev) =>
          prev.map((b) => (b.id === betId ? { ...b, ...patch } : b))
        );
      }
      showToast(
        'Votes did not match — second chance starts now. Both players must re-vote who won. Message your opponent first. If you both re-vote and still disagree, the 15-minute timer is void and a 24-hour vault lock starts immediately. You cannot send or agree to custom challenges with any player until the lock ends.',
        'critical'
      );
      return;
    }
    if (useCloudChallenges) {
      try {
        await updateChallenge(betId, { winnerVotes });
      } catch (e) {
        console.error(e);
      }
    } else {
      setMockBets((prev) =>
        prev.map((b) => (b.id === betId ? { ...b, winnerVotes } : b))
      );
    }
  };

  const handleDisputeRefund = async (betId) => {
    const bet = mockBets.find((b) => b.id === betId);
    if (useCloudChallenges) {
      try {
        await removeChallenge(betId);
      } catch (e) {
        console.error(e);
      }
    } else {
      setMockBets((prev) => prev.filter((b) => b.id !== betId));
    }
    if (bet) {
      const myStake = bet.participants?.find((p) => p.id === userWithFriends.id)?.amount;
      if (myStake) {
        await refundStakeToWallet(myStake, bet.id, 'Dispute refund');
      } else {
        setWallet((prev) => refundParticipantStakeOnState(prev, bet, userWithFriends.id));
      }
    }
    setUserStrikes((s) => {
      const next = Math.min(3, s + 1);
      if (next >= 3) setSuspensionEndsAt(Date.now() + 30 * 24 * 60 * 60 * 1000);
      return next;
    });
    showToast('Funds returned to both players. You received a strike. 3 strikes = 30-day account suspension.',)
  };

  // If the second-chance window ends and votes still don't match,
  // we escalate to a 24h escrow lock (DISPUTED) and trigger refunds later.
  useEffect(() => {
    let cancelled = false;

    const now = Date.now();
    const due = mockBets.filter(
      (b) => b.status === ChallengeStatus.SECOND_CHANCE && b.secondChanceEndsAt && b.secondChanceEndsAt <= now && !b.finalOutcome
    );
    if (!due.length) return undefined;

    (async () => {
      await Promise.all(
        due.map(async (bet) => {
          const ids = (bet.participants || []).map((p) => p.id);
          const votes = bet.winnerVotes || {};
          const voted = ids.filter((id) => votes[id]);
          const same = voted.length === 2 && votes[ids[0]] === votes[ids[1]];
          if (same) return;

          try {
            await applyCustomWagerDisputeEscalation(bet);
          } catch (e) {
            console.error('Second chance escalation failed:', e);
          }

          if (!cancelled && (bet.creatorId === userWithFriends?.id || bet.recipientId === userWithFriends?.id)) {
            showToast(
              'Second chance ended without agreement. Funds are locked for 24 hours, custom challenges are disabled with all players, and risk tally is applied.',
              'critical'
            );
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [mockBets, applyCustomWagerDisputeEscalation, userWithFriends?.id]);

  // When a custom wager escalates to a 24h escrow lock (DISPUTED),
  // mark each participating player with a persistent risk tally.
  useEffect(() => {
    if (!useCloudChallenges || !firebaseUser?.uid) return;
    let cancelled = false;

    const uid = firebaseUser.uid;
    const tasks = mockBets
      .filter((bet) => bet.status === ChallengeStatus.DISPUTED)
      .filter((bet) => !riskTalliedBetIdsRef.current.has(riskTallyEscalationKey(bet.id, uid)))
      .map(async (bet) => {
        const isParticipant =
          bet.creatorId === uid
          || bet.recipientId === uid
          || bet.participants?.some((p) => p.id === uid);
        if (!isParticipant) return;

        riskTalliedBetIdsRef.current.add(riskTallyEscalationKey(bet.id, uid));
        const lockUntil = bet.disputeEndsAt || Date.now() + DISPUTE_LOCK_MS;
        const penalty = await applyDisputePenaltyOnServer(lockUntil);
        if (!cancelled) {
          setAccountLockEndsAt((prev) => Math.max(prev || 0, lockUntil));
          setProfileDoc((prev) =>
            prev
              ? {
                  ...prev,
                  riskTally: penalty?.riskTally ?? (prev.riskTally ?? 0) + 1,
                  accountLockEndsAt: penalty?.accountLockEndsAt ?? lockUntil,
                }
              : prev
          );
          setFriends((prev) =>
            prev.map((f) =>
              (f.id === uid ? { ...f, riskTally: (f.riskTally ?? 0) + 1 } : f)
            )
          );
        }
      });

    Promise.all(tasks).catch((e) => console.error('risk tally effect failed', e));

    return () => {
      cancelled = true;
    };
  }, [mockBets, useCloudChallenges, firebaseUser?.uid]);

  const handleCreateChallengeTicket = async (payload) => {
    if (!ensureRegisteredForPlay()) return null;
    try {
      const result = await createChallengeTicket(payload);
      showToast('Challenge ticket created — share the link!', 'success');
      completeOnboardingTour();
      return result;
    } catch (e) {
      console.error(e);
      showToast(e?.message || 'Could not create challenge ticket.');
      return null;
    }
  };

  const handleCreateLiveWager = async (newBet) => {
    if (!ensureRegisteredForPlay()) return;
    const creatorId = firebaseUser?.uid || newBet?.creatorId;
    const recipientId = newBet?.recipientId;
    if (!creatorId || !recipientId) {
      showToast('Sign in and select an opponent to send a challenge.')
      return;
    }
    const betPayload = {
      ...newBet,
      creatorId,
      recipientId,
      participants: (newBet.participants || []).map((p) =>
        p.id === newBet.creatorId ? { ...p, id: creatorId } : p
      ),
    };
    const opponent = friends.find((f) => f.id === recipientId);
    if (!opponent) {
      showToast('You can only challenge friends on your squad.',)
      return;
    }
    if (isMessagingBlockedWithPlayer(recipientId, friends)) {
      showToast(opponent.blockedByMe
          ? 'You blocked this player. Unblock them in Friends to challenge them.'
          : 'You cannot challenge this player while they have you blocked.',)
      return;
    }
    const creatorStake =
      betPayload.participants?.find((p) => p.id === creatorId)?.amount ?? 0;

    if (useCloudChallenges) {
      const challengeRef = buildChallengeRef();
      const challengeId = challengeRef.id;
      betPayload.id = challengeId;

      try {
        const friendsOk = await areFriends(creatorId, recipientId);
        if (!friendsOk) {
          showToast('You must be friends before sending a challenge. Re-accept the friend request if needed.',)
          return;
        }

        await createChallenge({ ...betPayload, challengeType: 'live' }, challengeRef);

        if (!(await deductStakeForPlay(creatorStake, challengeId, 'Live challenge entry'))) {
          await removeChallenge(challengeId);
          return;
        }
      } catch (e) {
        console.error(e);
        try {
          await removeChallenge(betPayload.id);
        } catch (_) { /* doc may not exist */ }
        showToast(getChallengeSendErrorMessage(e, { isCustom: false }));
        return;
      }
    } else {
      if (!(await deductStakeForPlay(creatorStake, betPayload.id, 'Live challenge entry'))) return;
      setMockBets((prev) => [...prev, betPayload]);
    }
    showToast(
      `Challenge sent to ${opponent.name || 'opponent'}! ${betPayload.matchup.description} — waiting for accept or decline.`,
      'success'
    );
    completeOnboardingTour();
  };

  const handleRemoveFriend = async (friendId) => {
    if (!friendId) return;
    const friend = friends.find((f) => f.id === friendId);
    const name = friend?.name || 'Player';

    if (useCloudChallenges && firebaseUser?.uid) {
      try {
        const clearedRequestIds = await removeFriendship(firebaseUser.uid, friendId);
        clearedRequestIds.forEach((id) => declinedFriendRequestNotifiedRef.current.add(id));
        setFriends((prev) => prev.filter((f) => f.id !== friendId));
      } catch (err) {
        console.error(err);
        showToast('Could not remove friend. Try again.')
        return;
      }
    } else {
      setFriends((prev) => prev.filter((f) => f.id !== friendId));
    }

    setBlockedFriendIds((prev) => {
      const next = new Set(prev);
      next.delete(friendId);
      return next;
    });
    if (privateChatFriend?.id === friendId) {
      setPrivateChatFriend(null);
    }
    showToast(`Removed ${name} from your squad. They're removed on both sides — send a new friend request to reconnect.`)
  };

  const handleStartPrivateChat = (friendId) => {
    if (!ensureRegisteredForPlay()) return;
    if (!userCanUsePrivateChat) {
      showToast(FREE_PRIVATE_CHAT_MESSAGE)
      return;
    }
    tryOpenPrivateChat({
      friendId,
      friends,
      onOpen: setPrivateChatFriend,
      onNotify: (msg) => showToast(typeof msg === 'string' ? msg : msg?.message, 'critical'),
    });
  };

  const handleSubscribeTier = async (tierId, opts = {}) => {
    if (!canStartStripeCheckout(accessCtx)) {
      showGuestRegistrationPrompt();
      return;
    }
    if (opts.openCreateGroup) {
      setOpenCreateGroupAfterCheckout();
    } else if (opts.intent) {
      setCheckoutIntent(opts.intent);
    }
    setStripeCheckoutLoading(tierId);
    try {
      const { url } = await createSubscriptionCheckout(tierId, {
        userId: firebaseUser.uid,
        email: firebaseUser.email,
        checkoutSource: opts.source || CHECKOUT_SOURCES.WALLET_PLANS,
        checkoutDetail: opts.detail,
      });
      if (url) {
        showToast(SUBSCRIPTION_REDIRECT_TOAST, 'success');
        window.location.href = url;
      }
    } catch (e) {
      showToast(e?.message || 'Could not start checkout.')
    } finally {
      setStripeCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    if (!firebaseUser?.uid) return;
    setStripeCheckoutLoading('portal');
    try {
      const { url } = await createBillingPortalSession({ userId: firebaseUser.uid });
      if (url) {
        showToast(BILLING_PORTAL_REDIRECT_TOAST, 'success');
        window.location.href = url;
      }
    } catch (e) {
      showToast(e?.message || 'Could not open billing portal.')
    } finally {
      setStripeCheckoutLoading(null);
    }
  };

  const handleRequireRegistration = () => {
    if (!firebaseUser?.uid) navigate('/signup');
    else navigate('/create-profile');
  };

  const handleSendFriendRequest = async (player) => {
    if (!player?.id) return;
    if (!ensureRegisteredForPlay()) return;
    if (!canReceiveFriendRequest(firebaseUser?.uid)) {
      handleRequireRegistration();
      return;
    }
    if (!canReceiveFriendRequest(player.id)) return;
    if (friends.some((f) => f.id === player.id)) {
      showToast(`${player.name} is already in your squad.`, 'success');
      return;
    }
    if (!ensureFriendSlot()) return;
    if (hiddenPlayerIds.has(player.id)) {
      showToast(blockedFriendIds.has(player.id)
          ? 'This player is blocked. Unblock in Friends first.'
          : 'This player is not available.',)
      return;
    }

    if (!useCloudChallenges || !firebaseUser?.uid || !profileDoc) {
      if (!canAddFreeFriend(countUserAddedFriends(friends))) {
        showToast(FREE_FRIEND_LIMIT_MESSAGE)
        return;
      }
      setFriends((prev) => {
        if (prev.some((f) => f.id === player.id)) return prev;
        return stripLegacySystemFriends([
          ...prev,
          {
            id: player.id,
            name: player.name,
            username: player.username,
            avatar: player.avatar,
          },
        ]);
      });
      showToast(`${player.name} added to your squad.`, 'success');
      return;
    }

    try {
      await sendFriendRequest(firebaseUser.uid, player, profileDoc);
      showToast(`Friend request sent to ${player.name}. They can accept or decline.`, 'success');
    } catch (err) {
      const code = err?.message || '';
      if (code === 'REQUEST_ALREADY_PENDING') {
        showToast('A friend request is already pending with this player.')
      } else if (code === 'ALREADY_FRIENDS') {
        showToast(`${player.name} is already in your squad.`, 'success');
      } else if (err?.code === 'permission-denied') {
        console.error(err);
        showToast('Could not send friend request (Firestore permission denied). Restart emulators after rules change, or deploy firestore.rules.',)
      } else {
        console.error(err);
        showToast(`Could not send friend request. ${err?.message || 'Try again.'}`);
      }
    }
  };

  const handleAcceptFriendRequest = async (request) => {
    if (!firebaseUser?.uid || !profileDoc) return;
    if (!ensureFriendSlot()) return;
    try {
      await acceptFriendRequest(request);
      showToast(`${request.fromDisplayName || 'Player'} is now in your squad.`, 'success');
    } catch (err) {
      // Server is the authority on the cap; the client pre-check can be stale.
      if (err?.details?.reason === FRIEND_LIMIT_REACHED) {
        showToast(FREE_FRIEND_LIMIT_MESSAGE, 'critical');
        return;
      }
      console.error(err);
      showToast('Could not accept friend request.')
    }
  };

  const handleDeclineFriendRequest = async (requestId) => {
    if (!requestId) return;
    if (!useCloudChallenges) return;
    try {
      await declineFriendRequest(requestId);
      showToast('Friend request declined.')
    } catch (err) {
      console.error(err);
      showToast('Could not decline friend request.')
    }
  };

  const handleCancelFriendRequest = async (requestId) => {
    if (!requestId || !firebaseUser?.uid) return;
    if (!useCloudChallenges) return;
    declinedFriendRequestNotifiedRef.current.add(requestId);
    try {
      await cancelOutgoingFriendRequest(requestId, firebaseUser.uid);
      showToast('Friend request withdrawn. Send a new request anytime to reconnect.',)
    } catch (err) {
      console.error(err);
      showToast('Could not cancel friend request.')
    }
  };

  const handleBlockFriend = async (friendId) => {
    if (!friendId) return;
    if (useCloudChallenges && firebaseUser?.uid) {
      try {
        await setFriendBlocked(firebaseUser.uid, friendId, true);
      } catch (err) {
        console.error(err);
        showToast('Could not block player right now.')
        return;
      }
    } else {
      setBlockedFriendIds((prev) => new Set([...prev, friendId]));
      setFriends((prev) =>
        prev.map((f) =>
          f.id === friendId ? { ...f, blockedByMe: true, isBlocked: true, isHidden: true } : f
        )
      );
    }
    if (privateChatFriend?.id === friendId) {
      setPrivateChatFriend(null);
    }
    showToast('Player blocked. You both disappear from Find Ops, squad, and arena chat until you unblock them.',)
  };

  const clearPrivateChatStateForFriend = (friendId) => {
    if (privateChatFriend?.id === friendId) {
      setPrivateChatFriend(null);
    }
    clearInboxStateForFriend(friendId);
  };

  const handleUnblockFriend = async (friendId) => {
    if (!friendId) return;
    const friend = friends.find((f) => f.id === friendId);
    const name = friend?.name || 'Player';

    if (useCloudChallenges && firebaseUser?.uid) {
      try {
        const clearedRequestIds = await unblockAndResetRelationship(
          firebaseUser.uid,
          friendId
        );
        clearedRequestIds.forEach((id) => declinedFriendRequestNotifiedRef.current.add(id));
        setFriends((prev) => prev.filter((f) => f.id !== friendId));
      } catch (err) {
        console.error(err);
        showToast('Could not unblock player right now.')
        return;
      }
    } else {
      setBlockedFriendIds((prev) => {
        const next = new Set(prev);
        next.delete(friendId);
        return next;
      });
      setFriends((prev) => prev.filter((f) => f.id !== friendId));
    }

    clearPrivateChatStateForFriend(friendId);
    showToast(
      `Unblocked ${name}. Your private chat was cleared — send a new friend request to reconnect.`,
      'success'
    );
  };

  const handleUpdateProfilePicture = (url) => {
    if (url == null || url === '') {
      setUserAvatar(null);
      try { window.localStorage.removeItem(AVATAR_STORAGE_KEY); } catch (_) {}
      return;
    }
    setUserAvatar(url);
    try { window.localStorage.setItem(AVATAR_STORAGE_KEY, url); } catch (_) {}
  };

  const handlePrivateChatDeleted = (friendId) => {
    if (friendId) clearInboxStateForFriend(friendId);
    showToast('Chat history cleared.')
  };

  const handleDeleteAccount = async (password) => {
    const uid = firebaseUser?.uid;
    await deleteUserAccountPermanently(password);
    clearLocalUserSession(uid);
    clearSavedSession();
    setProfileDoc(null);
    setUserAvatar(null);
    setWallet(loadWallet(null));
    setPlayerStats({});
    setFriends([]);
    setEntered(false);
    setProfileOpen(false);
    navigate('/signup', { replace: true });
  };

  const enteredMainContent = isSuspended ? (
    <SuspensionGate suspensionEndsAt={suspensionEndsAt} onLeave={handleLeaveApp} />
  ) : (
    <>
      <HowToPlayModal
        open={howToPlayVisible({ showHowToPlay, showTour })}
        onClose={() => setShowHowToPlay(false)}
      />
      <GuestWelcomeTicket
        open={guestWelcomeVisible({ showGuestWelcome, showTour })}
        onDismiss={dismissGuestWelcome}
      />
      <GuidedSpotlightTour
        open={showTour}
        steps={tourSteps}
        onComplete={completeOnboardingTour}
        onSkip={completeOnboardingTour}
      />

      <StadiumShell className="md:p-0">
        <Suspense fallback={<AppBootFallback />}>
          <Lockbox
            user={userWithFriends}
            activeBets={mockBets.filter((b) => {
              const myId = userWithFriends.id;
              const otherId = b.creatorId === myId ? b.recipientId : b.creatorId;
              if (hiddenPlayerIds.has(otherId)) return false;
              // Free users keep seeing incoming custom challenges so they can
              // decline them (and get an upgrade moment on accept). They can
              // never be in a custom challenge beyond PENDING_AGREEMENT as a
              // recipient, so accept stays gated by the handler + rules.
              // Terminal phases (settled/declined/refunded/expired) drop out via
              // the canonical predicate — one classification, not a denylist.
              return !isTerminalChallenge(b);
            })}
            completedBets={mockBets.filter((b) => {
              // Sprint 9 WI-3: terminal challenges the viewer is a party to,
              // surfaced read-only in My Arena → Completed so a finished
              // challenge never just disappears. Same source as activeBets
              // (one classification) — the complement of the active filter.
              const myId = userWithFriends.id;
              const otherId = b.creatorId === myId ? b.recipientId : b.creatorId;
              if (hiddenPlayerIds.has(otherId)) return false;
              return isTerminalChallenge(b);
            })}
            hasLiveSubscription={hasLiveSubscription}
            canSeeScoreStreaks={userCanSeeScoreStreaks}
            canUseCustomWagers={userCanUseCustomWagers}
            subscriptionTier={subscriptionTier}
            subscriptionStatus={subscriptionStatus}
            subscriptionProfile={accessProfile}
            onSubscribeTier={handleSubscribeTier}
            onManageBilling={handleManageBilling}
            stripeConfigured={isStripeConfigured()}
            stripeCheckoutLoading={stripeCheckoutLoading}
            onRefreshProfile={refreshProfileAndWallet}
            onClaimWinnings={handleClaimWinnings}
            onReportGameFinal={handleReportGameFinal}
            onUpdateBetMatchup={handleUpdateBetMatchup}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onCounter={handleCounter}
            onTabChange={handleTabChange}
            onCreateCustomWager={handleCreateCustomWager}
            onCreateOpenChallenge={handleCreateOpenChallenge}
            onAcceptOpenChallenge={handleAcceptOpenChallenge}
            onCancelOpenChallenge={handleCancelOpenChallenge}
            onRequireRegistration={handleRequireRegistration}
            onGuestActionBlocked={showGuestRegistrationPrompt}
            onNotify={(message) => showToast(message, 'critical')}
            onToast={showToast}
            onCompletedTransition={notifyCompletedTransition}
            onAgreeCustomWager={handleAgreeCustomWager}
            onDeclineCustomWager={handleDeclineCustomWager}
            onSubmitWinner={handleSubmitWinner}
            onDisputeRefund={handleDisputeRefund}
            userStrikes={userStrikes}
            isSuspended={isSuspended}
            onRemoveFriend={handleRemoveFriend}
            onStartPrivateChat={handleStartPrivateChat}
            onSendFriendRequest={handleSendFriendRequest}
            incomingFriendRequests={incomingFriendRequests.filter(
              (r) => !hiddenPlayerIds.has(r.fromUserId)
            )}
            outgoingFriendRequests={outgoingFriendRequests.filter(
              (r) => !hiddenPlayerIds.has(r.toUserId)
            )}
            onAcceptFriendRequest={handleAcceptFriendRequest}
            onDeclineFriendRequest={handleDeclineFriendRequest}
            onCancelFriendRequest={handleCancelFriendRequest}
            useCloudFriends={useCloudSocial}
            currentUserId={currentChatUid}
            blockedFriendIds={blockedFriendIds}
            hiddenPlayerIds={hiddenPlayerIds}
            onBlockFriend={handleBlockFriend}
            onUnblockFriend={handleUnblockFriend}
            onCreateLiveWager={handleCreateLiveWager}
            onCreateChallengeTicket={handleCreateChallengeTicket}
            onUpdateProfilePicture={handleUpdateProfilePicture}
            onDeleteAccount={handleDeleteAccount}
            canDeleteAccount={useCloudSocial && Boolean(firebaseUser?.uid)}
            deleteAccountReauthMethod={
              firebaseUser ? getAccountReauthMethod(firebaseUser) : 'password'
            }
            profileOpenFromParent={profileOpen}
            onProfileClose={() => setProfileOpen(false)}
            privateChatWith={privateChatFriend}
            onClosePrivateChat={() => setPrivateChatFriend(null)}
            onPrivateChatDeleted={handlePrivateChatDeleted}
            unreadMessageCount={unreadMessageCount}
            unreadByFriendId={unreadByFriendId}
            playerStats={playerStats}
            playerStatsReady={playerStatsReady}
            onRecordBetResult={recordBetResult}
            onValidateStakeForPlay={validateStakeForPlay}
            riskTallyByUserId={riskTallyByUserId}
            isAccountLocked={isAccountLocked}
            accountLockEndsAt={accountLockEndsAt}
            allCachedGames={homepage.lookupGames}
            eventsLoading={homepage.showGamesSkeleton}
            showWalletSkeleton={homepage.showWalletSkeleton}
            gamesFreshnessLabel={homepage.freshnessLabel}
            homepageError={homepage.error}
            hydrationPhase={homepage.hydrationPhase}
            openFriendsSignal={openFriendsSignal}
            openPendingInboxSignal={openPendingInboxSignal}
            bootstrapAction={bootstrapAction}
            onBootstrapConsumed={() => setBootstrapAction(null)}
            pendingGroupInvites={pendingGroupInvites}
            groupMembershipCount={groupMemberships.length}
            onSignOut={handleLeaveApp}
            onHowToPlayClick={openHowToPlay}
            onWelcomeGuideClick={openTourReplay}
            walletChromeRef={walletChromeRef}
            userAvatar={userWithFriends?.avatar}
            showNotifications={Boolean(firebaseUser?.uid)}
            notificationProps={
              firebaseUser?.uid
                ? {
                    items: notificationCenter.items,
                    unreadCount: notificationCenter.unreadCount,
                    open: notificationCenter.open,
                    onToggle: () => notificationCenter.setOpen((v) => !v),
                    onMarkRead: notificationCenter.markRead,
                    onMarkAllRead: notificationCenter.markAllRead,
                    onAction: handleNotificationAction,
                  }
                : null
            }
            onLeaveClick={handleLeaveApp}
            onDesktopProfileClick={() => setProfileOpen(true)}
          />
        </Suspense>
      </StadiumShell>
    </>
  );

  return (
    <div className="App min-h-screen bg-surface-app text-white">
      <AppToast toast={toast} onDismiss={() => setToast(null)} />

      <WinnerNotificationModal
        open={notification.show && Boolean(notification.winner) && !notification.winner?.isDraw}
        winner={notification.winner}
        autoReleased={notification.autoReleased}
        onClose={() => setNotification({ show: false, message: '', winner: null, chatFrom: null })}
      />

      <GuestRegistrationPrompt
        open={guestPromptOpen}
        onClose={() => setGuestPromptOpen(false)}
        onCreateAccount={() => {
          setGuestPromptOpen(false);
          handleRequireRegistration();
        }}
      />

      {adminRoute ? (
        <AdminRouteErrorBoundary resetKey={location.pathname}>
          {adminRoute === 'finance' ? <AdminFinancePage /> : <AdminGroupsPage />}
        </AdminRouteErrorBoundary>
      ) : location.pathname === '/groups' ? (
        <GroupsPage />
      ) : location.pathname.startsWith('/groups/') ? (
        <GroupDetailPage />
      ) : location.pathname === '/signup' ? (
        <AuthPage />
      ) : location.pathname === '/login' ? (
        <Navigate to="/signup?mode=login" replace />
      ) : location.pathname === '/create-profile' ? (
        <CreateProfilePage
          authReady={authReady}
          firebaseUser={firebaseUser}
          profileDoc={profileDoc}
          onProfileComplete={(doc) => {
            setProfileDoc(doc);
            if (firebaseUser?.uid) saveCachedProfile(firebaseUser.uid, doc);
            if (profileIsComplete(doc)) {
              justCreatedProfileRef.current = true;
              setEntered(true);
            }
          }}
          onProfileAlreadyExists={() => {
            if (profileIsComplete(profileDoc)) {
              setEntered(true);
              setShowHowToPlay(false);
            }
          }}
        />
      ) : location.pathname.startsWith('/ticket/') ? (
        <ChallengeTicketRouteErrorBoundary resetKey={location.pathname}>
          <ChallengeTicketPage onEnterLockbox={() => setEntered(true)} />
        </ChallengeTicketRouteErrorBoundary>
      ) : location.pathname.startsWith('/join/') ? (
        <JoinLeaguePage />
      ) : isAppEntryAlias(location.pathname) ? (
        <Navigate to="/app" replace />
      ) : onAppEntryRoute ? (
        !authReady ? (
          <AppBootFallback />
        ) : entered ? (
          enteredMainContent
        ) : (
          <AppBootFallback />
        )
      ) : (
        <PublicSite />
      )}
    </div>
  );
}

export default App;
